import Modal from "bootstrap/js/dist/modal";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { BankAccountCard } from "../components/bankCard";
import { categoryButton, customAcceptButton, customModalCancelButton, fundButton, tabButton, themeBasedPurchaseFormControl } from "../components/customStyles";
import FormattedNumber from "../components/formattedNumber";
import FundItem from "../components/fund";
import { CrossIcon } from "../components/icons";
import { customToast } from "../components/toasts";
import { getFundBankAccounts, getFundsByAmcCode } from "../services/funds.service";
import { sendInvestorRequest } from "../services/invest.service";
import { useAuth } from "../store/AuthContext";
import { amountToWords, format2, format4, numberWithCommas, separateDecimal } from "../utils/numbers";

import { Calculator, EllipsisVertical } from "lucide-react";
import { Tooltip } from "react-tooltip";
import ImageUpload from "../components/imageUpload";
import { useI18n } from "../hooks/useI18n";

function groupByCategory(items = [], opts = { normalize: true }) {
  const { normalize } = opts;
  const groups = new Map();

  for (const item of items) {
    let key = item?.nature;
    if (normalize) {
      key = typeof key === "string" ? key.trim() : key;
    }
    const name = key && key !== "" ? key : "Unspecified";

    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(item);
  }

  // return as array preserving order of first appearance
  return Array.from(groups.entries()).map(([name, items]) => ({
    name,
    items,
    count: items.length,
  }));
}

function getNormalizedScroll(el) {
  if (!el) return { normalized: 0, max: 0 };

  const scrollLeft = el.scrollLeft;
  const max = Math.max(0, el.scrollWidth - el.clientWidth);
  const dir = getComputedStyle(el).direction; // 'ltr' or 'rtl'

  if (dir !== "rtl") {
    // LTR: scrollLeft already measures from 0..max
    return { normalized: Math.max(0, scrollLeft), max };
  }

  // RTL: several browser behaviours:
  // - Firefox: scrollLeft goes 0..-max (negative)
  // - WebKit/Chromium: scrollLeft goes max..0 (positive but reversed)
  // We'll detect sign and normalize:
  if (scrollLeft < 0) {
    // Firefox-like
    return { normalized: Math.abs(scrollLeft), max };
  } else {
    // Webkit-like (positive reversed)
    // normalized = max - scrollLeft
    return { normalized: Math.max(0, max - scrollLeft), max };
  }
}

const Funds = () => {
  // const [fundsData, setFundsData] = useState([]);
  const { funds, loadingFunds } = useOutletContext();
  const [visibleData, setVisibleData] = useState([]);
  const [tabScrollingLeft, setTabScrollingLeft] = useState(false);
  const [tabScrollingRight, setTabScrollingRight] = useState(false);
  const [fundScrollLeft, setFundScrollLeft] = useState(false);
  const [fundScrollRight, setFundScrollRight] = useState(false);
  const [categoryScrollLeft, setCategoryScrollLeft] = useState(false);
  const [categoryScrollRight, setCategoryScrollRight] = useState(false);
  const [amountScrollLeft, setAmountScrollLeft] = useState(false);
  const [amountScrollRight, setAmountScrollRight] = useState(false);
  // const [investAmount, setInvestAmount] = useState("100000");
  const [activeTabItem, setActiveTabItem] = useState({ type: "all", name: "All Funds" });
  const [activePurchaseFund, setActivePurchaseFund] = useState(null);
  const [activeFundBankAccounts, setActiveFundBankAccounts] = useState(null);
  const [authorized, setAuthorized] = useState(true);
  // const [activeModalStep, setActiveModalStep] = useState(0);
  const [purchaseData, setPurchaseData] = useState(() => ({
    fund_units: "",
    total_amount: "",
  }));
  const [fundPaymentData, setFundPaymentData] = useState(() => ({
    bank_account: "",
    bank_name: "",
    branch_name: "",
    account_title: "",
    iban: "",
    payment_method: "",
    payment_gateway: "raast",
    payment_instrument: "",
    instrument_no: "",
    instrument_date: new Date().toISOString().split("T")[0],
    comment: "",
    payment_proof_file: null,
  }));
  const [purchaseFundStep, setPurchaseFundStep] = useState(0);
  const [loadingMoreData, setLoadingMoreData] = useState(false);
  const [fetchingFunds, setFetchingFunds] = useState(false);
  const [retryScrollCount, setRetryScrollCount] = useState(0);
  const [loading, setLoading] = useState(() => ({
    funds: false,
    purchaseFund: false,
    paymentFund: false,
    finalPaymentFund: false,
    bankAccounts: false,
  }));
  const [screenSize, setScreenSize] = useState();

  const [collapsedText, setCollapsedText] = useState(true);
  const [selectFund, setSelectFund] = useState(() => ({
    category: { name: "All" },
    fund: null,
    search: "",
    fund_units: "",
    total_amount: "",
  }));

  const [showSearchSelectFund, setShowSearchSelectFund] = useState(false);
  // const [showFundsDetail, setShowFundsDetail] = useState(() => ({
  //   show: false,
  //   symbol: "",
  // }));
  const { showDetails, setShowDetails, setActiveMenuItem } = useOutletContext();
  // console.log(showDetails);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // const searchTerm = searchParams.get("search");
  // const searchQuery = searchParams.get("query");
  const purchaseSearchModal = searchParams.get("modal");
  const purchaseSearchSymbol = searchParams.get("symbol");

  const { user } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isRTL } = useI18n();

  const purchaseProcess = useRef("");
  const purchaseFromCalculator = useRef(false);
  const dataOnModalClose = useRef({ ...purchaseData, ...fundPaymentData, activeFund: activePurchaseFund });
  const modalCloseRequest = useRef(false);
  const paymentCheckProcess = useRef(false);
  const selectSearchFundRef = useRef(null);
  const backButtonPress = useRef(false);

  const fundsModalRef = useRef(null);
  const modalRef = useRef(null);

  const helpModal = useRef(null);
  const helpModalInstance = useRef(null);

  const paymentModal = useRef(null);
  const paymentModalInstance = useRef(null);

  const paymentProofModal = useRef(null);
  const paymentProofModalInstance = useRef(null);

  // const { paymentCheckModal, ask, answer } = usePaymentCheck();

  const paymentCheckModal = useRef(null);
  const paymentCheckModalInstance = useRef(null);

  const tabsScrollRef = useRef(null);
  const fundsContainerRef = useRef(null);
  const calculatorFundRef = useRef(null);
  const calculatorCategoryRef = useRef(null);
  const calculatorAmountRef = useRef(null);
  const derivedFundsRef = useRef(null);
  const imageUploadRef = useRef(null);

  const observer = useRef();

  // const finalFundsData = activeTabItem.name !== "All Funds" ? fundsData?.filter((item) => item.nature === activeTabItem?.name) : fundsData;

  const filteredFunds = useMemo(() => {
    if (activeTabItem?.name === "All Funds") return funds;

    return funds?.filter((f) => f.nature === activeTabItem?.name);
  }, [funds, activeTabItem]);

  const searchSelectFunds = useMemo(() => {
    const category = selectFund?.category?.name ?? "All";
    const searchTerm = (selectFund?.search ?? "").trim().toLowerCase();

    let base = Array.isArray(funds) ? funds : [];

    if (category !== "All") {
      base = base.filter((item) => (item?.nature ?? "") === category);
    }

    if (!searchTerm) return base;

    const matches = (item) => {
      const symbol = String(item?.symbol_code ?? "").toLowerCase();
      const name = String(item?.fund_name ?? "").toLowerCase();
      return symbol.includes(searchTerm) || name.includes(searchTerm);
    };

    return base.filter(matches);
  }, [selectFund.search, selectFund.category, funds]);

  // const searchSelectFunds =
  //   selectFund.category.name !== "All"
  //     ? fundsData?.filter((item) => item?.nature === selectFund.category.name)
  //     : selectFund.search.length !== 0
  //       ? fundsData?.filter((item) => (item?.symbol_code?.toLowerCase().includes(selectFund?.search) ? true : item?.fund_name?.toLowerCase().includes(selectFund.search) ? true : false))
  //       : fundsData;

  const fundCategories = groupByCategory(funds);

  const tabsRowScrollHandler = () => {
    const el = tabsScrollRef.current;
    if (!el) return;

    const { normalized, max } = getNormalizedScroll(el);
    const EPS = 1; // tolerance for fractional pixels

    setTabScrollingLeft(normalized > EPS); // can scroll left
    setTabScrollingRight(normalized < max - EPS); // can scroll right
    // if (tabsScrollRef.current?.scrollLeft > 0) {
    //   setTabScrollingLeft(true);
    // } else {
    //   setTabScrollingLeft(false);
    // }

    // if (Math.ceil(tabsScrollRef.current?.clientWidth + tabsScrollRef.current?.scrollLeft) >= tabsScrollRef.current?.scrollWidth) {
    //   setTabScrollingRight(false);
    // } else {
    //   setTabScrollingRight(true);
    // }
  };
  const fundScrollHandler = () => {
    const el = calculatorFundRef.current;
    if (!el) return;

    const { normalized, max } = getNormalizedScroll(el);
    const EPS = 1; // tolerance for fractional pixels

    setFundScrollLeft(normalized > EPS); // can scroll left
    setFundScrollRight(normalized < max - EPS); // can scroll right
    // if (calculatorFundRef.current?.scrollLeft > 0) {
    //   setFundScrollLeft(true);
    // } else {
    //   setFundScrollLeft(false);
    // }

    // if (Math.ceil(calculatorFundRef.current?.clientWidth + calculatorFundRef.current?.scrollLeft) >= calculatorFundRef.current?.scrollWidth) {
    //   setFundScrollRight(false);
    // } else {
    //   setFundScrollRight(true);
    // }
  };
  const categoryScrollHandler = () => {
    const el = calculatorCategoryRef.current;
    if (!el) return;

    const { normalized, max } = getNormalizedScroll(el);
    const EPS = 1; // tolerance for fractional pixels

    setCategoryScrollLeft(normalized > EPS); // can scroll left
    setCategoryScrollRight(normalized < max - EPS); // can scroll right

    // if (calculatorCategoryRef.current?.scrollLeft > 0) {
    //   setCategoryScrollLeft(true);
    // } else {
    //   setCategoryScrollLeft(false);
    // }

    // if (Math.ceil(calculatorCategoryRef.current?.clientWidth + calculatorCategoryRef.current?.scrollLeft) >= calculatorCategoryRef.current?.scrollWidth) {
    //   setCategoryScrollRight(false);
    // } else {
    //   setCategoryScrollRight(true);
    // }
  };
  const calculatorAmountHandler = () => {
    const el = calculatorAmountRef.current;
    if (!el) return;

    const { normalized, max } = getNormalizedScroll(el);
    const EPS = 1; // tolerance for fractional pixels

    setAmountScrollLeft(normalized > EPS); // can scroll left
    setAmountScrollRight(normalized < max - EPS); // can scroll right

    // if (calculatorCategoryRef.current?.scrollLeft > 0) {
    //   setCategoryScrollLeft(true);
    // } else {
    //   setCategoryScrollLeft(false);
    // }

    // if (Math.ceil(calculatorCategoryRef.current?.clientWidth + calculatorCategoryRef.current?.scrollLeft) >= calculatorCategoryRef.current?.scrollWidth) {
    //   setCategoryScrollRight(false);
    // } else {
    //   setCategoryScrollRight(true);
    // }
  };

  const handleCalculatorChange = (e) => {
    const { name, value } = e.target;

    const decimalLimit = name === "total_amount" ? 4 : 4;
    let formatted = "";

    const integerLimit = 12;

    if (value !== "") {
      let hasDecimal = false;
      let decimalCount = 0;
      let integerCount = 0;

      for (const x of value) {
        if (x === ".") {
          // allow only the first dot
          if (!hasDecimal) {
            formatted += x;
            hasDecimal = true;
          }
        } else if (x >= "0" && x <= "9") {
          if (hasDecimal) {
            // after the dot, only up to 4 digits
            if (decimalCount < decimalLimit) {
              formatted += x;
              decimalCount += 1;
            }
          } else {
            // before the dot, all digits allowed
            if (integerCount < integerLimit) {
              formatted += x;
              integerCount++;
            }
            // formatted += x;
          }
        } else if (x === ",") {
          // keep commas for now (you strip them below)
          formatted += x;
        }
        // everything else is skipped
      }
    }

    formatted = formatted.split(",").join("");

    setSelectFund((prev) => ({ ...prev, [name]: formatted }));

    if (name === "fund_units") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const calcAmount = format4(parsed * selectFund.fund?.navData?.offer_price);

        setSelectFund((prev) => ({ ...prev, total_amount: calcAmount }));
      } else {
        setSelectFund((prev) => ({ ...prev, total_amount: "" }));
      }
    } else if (name === "total_amount") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const calcUnits = parsed / selectFund.fund?.navData?.offer_price || 0;

        setSelectFund((prev) => ({ ...prev, fund_units: Number.isFinite(calcUnits) ? format4(calcUnits) : "0.0000" }));
      } else {
        setSelectFund((prev) => ({ ...prev, fund_units: "" }));
      }
    }
  };

  const handlePurchaseFundChange = (e) => {
    const { name, value } = e.target;

    const decimalLimit = name === "total_amount" ? 4 : 4;
    let formatted = "";

    const integerLimit = 12;

    if (value !== "") {
      let hasDecimal = false;
      let decimalCount = 0;
      let integerCount = 0;

      for (const x of value) {
        if (x === ".") {
          // allow only the first dot
          if (!hasDecimal) {
            formatted += x;
            hasDecimal = true;
          }
        } else if (x >= "0" && x <= "9") {
          if (hasDecimal) {
            // after the dot, only up to 4 digits
            if (decimalCount < decimalLimit) {
              formatted += x;
              decimalCount += 1;
            }
          } else {
            // before the dot, all digits allowed
            if (integerCount < integerLimit) {
              formatted += x;
              integerCount++;
            }
            // formatted += x;
          }
        } else if (x === ",") {
          // keep commas for now (you strip them below)
          formatted += x;
        }
        // everything else is skipped
      }
    }

    formatted = formatted.split(",").join("");

    // if (name === "fund_units" && value !== '') {
    //   formatted = format4(parseFloat(formatted));
    // } else if (name === "total_amount" && value !== '') {
    //   formatted = format2(parseFloat(formatted));
    // }

    setPurchaseData((prev) => ({ ...prev, [name]: formatted }));

    if (name === "fund_units") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const calcAmount = format4(parsed * activePurchaseFund?.navData?.offer_price);

        setPurchaseData((prev) => ({ ...prev, total_amount: calcAmount }));
      } else {
        setPurchaseData((prev) => ({ ...prev, total_amount: "" }));
      }
    } else if (name === "total_amount") {
      const parsed = parseFloat(formatted);

      if (!isNaN(parsed)) {
        const calcUnits = parsed / activePurchaseFund?.navData?.offer_price || 0;

        setPurchaseData((prev) => ({ ...prev, fund_units: Number.isFinite(calcUnits) ? format4(calcUnits) : "0.0000" }));
      } else {
        setPurchaseData((prev) => ({ ...prev, fund_units: "" }));
      }
    }
  };

  const updateFundPaymentData = (e, isBankAccount) => {
    if (isBankAccount) {
      if (e) {
        setFundPaymentData((prev) => ({
          ...prev,
          bank_account: e,
          bank_name: e.bank_name,
          branch_name: e.branch_name,
          account_title: e.account_title,
          iban: e.account_no,
        }));
      } else {
        setFundPaymentData((prev) => ({
          ...prev,
          bank_account: "",
          bank_name: "",
          branch_name: "",
          account_title: "",
          iban: "",
        }));
      }
    } else {
      const { name, value } = e.target;

      setFundPaymentData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const lastElementRef = useCallback(
    (node) => {
      if (observer.current) {
        // Disconnecting existing observer
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (!(visibleData.length <= filteredFunds.length)) return;

          if (activeTabItem?.name !== "All Funds") {
            setVisibleData(filteredFunds);
            return;
          }

          setLoadingMoreData(true);

          setTimeout(() => {
            setVisibleData((prev) => {
              if (prev.length >= filteredFunds.length) return prev;

              const nextLength = prev.length + 20;
              const nextSlice = filteredFunds.slice(0, nextLength);

              return nextSlice;
            });

            setLoadingMoreData(false);

            // if (activeTabItem.name === "All Funds") {
            //   setVisibleData((prevItems) => fundsData.slice(0, prevItems.length + 20));
            // } else {
            //   const categoryFunds = fundsData?.filter(item => item.nature === activeTabItem.nature);
            //   setVisibleData((prevItems) => categoryFunds.slice(0, prevItems.length + 20));
            // }
          }, 1000);
        }
      });

      if (node) observer.current.observe(node);
    },
    [filteredFunds],
  );

  // const updateFundsData = (data) => {
  //   setFundsData(data);
  // };

  const updateVisibleData = (data) => {
    setVisibleData(data);
  };

  const handleLeftScroll = () => {
    tabsScrollRef.current.scrollLeft -= 60;
  };

  const handleRightScroll = () => {
    tabsScrollRef.current.scrollLeft += 60;
  };

  const handlePurchaseFund = async (e) => {
    e.preventDefault();

    let status = true;

    try {
      setLoading((prev) => ({ ...prev, purchaseFund: true }));

      await new Promise((res) => setTimeout(() => res(), 1000));

      if (purchaseData.total_amount === "") {
        toast((ts) => customToast(t("invest.messages.error.amount"), "error", ts), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "300px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;
        return;
      }

      if (purchaseData.fund_units === "") {
        toast((ts) => customToast(t("invest.messages.error.units"), "error", ts), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "300px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;
        return;
      }

      let checkUnitsZero = false;
      let checkAmountZero = false;

      if (purchaseData.fund_units && purchaseData.fund_units !== "" && purchaseData.fund_units?.length !== 0) {
        if (parseFloat(purchaseData.fund_units) === 0 || parseFloat(purchaseData.fund_units) < 1) {
          checkUnitsZero = true;
        }
      } else {
        checkUnitsZero = true;
      }

      if (purchaseData.total_amount && purchaseData.total_amount !== "" && purchaseData.total_amount?.length !== 0) {
        if (parseFloat(purchaseData.total_amount) === 0 || parseFloat(purchaseData.total_amount) < 1) {
          checkAmountZero = true;
        }
      } else {
        checkAmountZero = true;
      }

      if (checkUnitsZero || checkAmountZero) {
        toast((ts) => customToast("Please enter a valid value greater than zero to proceed with the transaction.", "error", ts), {
          duration: 4000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "400px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        toast((ts) => customToast("Amount and units cannot be zero.", "error", ts), {
          duration: 4000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "340px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        return;
      }
      // const response = await getFundBankAccounts({ fund_code: activePurchaseFund?.symbol_code, email: user.data.email });
    } finally {
      setLoading((prev) => ({ ...prev, purchaseFund: false }));
    }

    if (status) {
      purchaseProcess.current = "payment";
      togglePaymentModal();
    }
  };

  const handleFundPayment = async (e) => {
    e.preventDefault();

    let status = true;

    try {
      setLoading((prev) => ({ ...prev, paymentFund: true }));

      await new Promise((res) => setTimeout(() => res(), 1000));

      if (!fundPaymentData.bank_account || fundPaymentData.bank_account === "") {
        toast((ts) => customToast(t("invest.messages.error.bank"), "error", ts), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "340px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        status = false;
      }

      // const response = await getFundBankAccounts({ fund_code: activePurchaseFund?.symbol_code, email: user.data.email });
    } finally {
      setLoading((prev) => ({ ...prev, paymentFund: false }));
    }

    if (status) {
      // let lastCheck = false;

      if (paymentCheckModal.current) {
        paymentCheckModalInstance.current.toggle();
      }
      // if (paymentCheck) {
      // 	purchaseProcess.current = 'final';
      // 	togglePaymentProofModal();
      // }
    }
  };

  const verifyUserPaymentChoice = () => {
    if (paymentCheckProcess.current) {
      purchaseProcess.current = "final";
      if (paymentModal.current) {
        paymentModalInstance.current.hide();
      }
      togglePaymentProofModal();
    }
  };

  const handleFundPurchaseFinal = async (e) => {
    e.preventDefault();
    modalCloseRequest.current = true;

    try {
      setLoading((prev) => ({ ...prev, finalPaymentFund: true }));

      await new Promise((res) => setTimeout(() => res(), 1000));

      const gross_amount = parseFloat(activePurchaseFund?.navData?.nav) * parseFloat(purchaseData?.fund_units)
      const sale_load = (parseFloat(activePurchaseFund?.navData?.offer_price) * parseFloat(purchaseData?.fund_units)) - gross_amount;
      const net_amount = (parseFloat(activePurchaseFund?.navData?.offer_price) * parseFloat(purchaseData?.fund_units)) - sale_load;

      const argsData = {
        email: user.data.email,
        fund_code: activePurchaseFund?.symbol_code,
        folio_no: user.data.folio_no,
        request_type: "saleofunit",
        payment_method: fundPaymentData.payment_gateway === "raast" ? "IBFT" : "Deposit",
        units_requested: purchaseData.fund_units,
        gross_amount_limit: purchaseData.total_amount,
        net_amount: format4(net_amount),
        sale_load: format4(sale_load),
        nav_date: activePurchaseFund?.navData?.nav_date,
        nav_at_request: activePurchaseFund?.navData.nav,
        investor_comment: fundPaymentData.comment,
        bank_account_code: fundPaymentData.bank_account?.account_code || "",
        investor_bank_name: fundPaymentData.bank_name,
        investor_branch_name: fundPaymentData.branch_name,
        investor_bank_iban: fundPaymentData.iban,
        // investor_comment: fundPaymentData.comment || null,
        investor_account_title: fundPaymentData.account_title,
        payment_proof_file: fundPaymentData.payment_proof_file || null,
      };

      const response = await sendInvestorRequest(argsData);

      if (!response.error) {
        toast((t) => customToast(response.message, "success", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "300px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });

        imageUploadRef.current?.reset();

        if (paymentProofModal.current) {
          paymentProofModalInstance.current.hide();
        }
      } else {
        toast((t) => customToast(response.message, "error", t), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            width: "300px",
            maxWidth: "100%",
            // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
            // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
            color: "rgb(33,37,41)",
            backgroundColor: "rgb(221, 204, 225)",
          },
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, finalPaymentFund: false }));
    }
  };

  const handlePaymentProofUpload = useCallback(
    (file) => {
      if (!file) {
        // setFundPaymentData((prev) => ({ ...prev, payment_proof_file: null }));
        setFundPaymentData((prev) => {
          if (!prev.payment_proof_file) return prev;
          return { ...prev, payment_proof_file: null };
        });
        return;
      }

      const allowedExts = ["png", "jpg", "jpeg", "webp"];

      if (!allowedExts.includes(file.name.split(".").pop().toLowerCase())) {
        toast((ts) => customToast(t("invest.messages.error.file"), "error", ts), {
          duration: 3000,
          removeDelay: 1000,
          style: {
            padding: "0.75rem",
            margin: "0",
            minWidth: "200px",
            backgroundColor: "rgb(255,255,255)",
          },
        });
        return;
      }

      setFundPaymentData((prev) => {
        const cur = prev.payment_proof_file;
        // guard: compare by name/size/lastModified to avoid rerenders when same file
        const isSame = cur && file && cur.name === file.name && cur.size === file.size && cur.lastModified === file.lastModified;

        if (isSame) return prev; // no update => no rerender loop
        return { ...prev, payment_proof_file: file };
      });
    },
    [setFundPaymentData],
  );

  const handleFundPaylater = async () => {
    modalCloseRequest.current = true;

    const argsData = {
      email: user.data.email,
      fund_code: activePurchaseFund?.symbol_code,
      folio_no: user.data.folio_no,
      request_type: "saleofunit",
      payment_method: fundPaymentData.payment_gateway === "raast" ? "IBFT" : "Deposit",
      units_requested: purchaseData.fund_units,
      gross_amount_limit: purchaseData.total_amount,
      nav_at_request: activePurchaseFund?.navData?.nav,
      investor_comment: fundPaymentData.comment,
      bank_account_code: fundPaymentData.bank_account?.account_code || "",
      payment_proof_file: fundPaymentData.payment_proof_file || null,
    };

    const response = sendInvestorRequest(argsData);

    if (paymentProofModal.current) {
      imageUploadRef.current?.reset();
      paymentProofModalInstance.current.hide();
    }
  };

  const sendRequestOnModalClose = async () => {
    const dataRef = dataOnModalClose.current;

    const argsData = {
      email: user.data.email,
      fund_code: dataRef.activeFund?.symbol_code,
      folio_no: user.data.folio_no,
      request_type: "saleofunit",
      payment_method: dataRef.payment_gateway === "raast" ? "IBFT" : "Deposit",
      units_requested: dataRef.fund_units,
      gross_amount_limit: dataRef.total_amount,
      nav_at_request: dataRef.activeFund?.navData?.nav,
      investor_comment: dataRef.comment,
      bank_account_code: dataRef.bank_account?.account_code || "",
      payment_proof_file: dataRef.payment_proof_file || null,
    };

    const response = await sendInvestorRequest(argsData);

    // resetAllData();
    purchaseProcess.current = "";
    modalCloseRequest.current = false;

    if (paymentProofModal.current) {
      imageUploadRef.current?.reset();
      paymentProofModalInstance.current.hide();
    }
  };

  const handlePaymentBackButton = () => {
    if (purchaseFromCalculator.current) {
      purchaseProcess.current = "";

      backButtonPress.current = true;

      if (paymentModal.current) {
        paymentModalInstance.current.toggle();
      }

      // purchaseFromCalculator.current = false;

      helpModalInstance.current.toggle();
    } else {
      purchaseProcess.current = "back";

      if (paymentModal.current) {
        paymentModalInstance.current.toggle();
      }

      modalRef.current.toggle();
    }
  };

  const togglePaymentProofModal = () => {
    if (paymentModal.current) {
      paymentModalInstance.current.hide();
    }

    paymentProofModalInstance.current.toggle();
  };

  const togglePaymentModal = () => {
    paymentCheckProcess.current = false;
    if (fundsModalRef.current) {
      modalRef.current.hide();
    }

    paymentModalInstance.current.toggle();
  };

  const toggleFundsModal = (fundData, initialPurchaseData) => {
    if (!user) {
      navigate("/unlock");
      return;
    } else if (user.requiresDocumentVerification) {
      navigate("/verify");
      return;
    } else {
      setAuthorized(true);

      purchaseProcess.current = "purchase";
      modalRef.current.toggle();

      if (fundData) {
        // setActiveModalStep(1);
        setActivePurchaseFund(fundData);

        if (initialPurchaseData) {
          setPurchaseData(() => ({
            total_amount: initialPurchaseData.amount ? format2(initialPurchaseData.amount) : "",
            fund_units: initialPurchaseData.units ? format4(initialPurchaseData.units) : "",
          }));
        }
      }
    }
    // navigate('/auth/verify');
    // } else {
    // 	modalRef.current.toggle();

    // 	setActivePurchaseFund(data);
    // }
  };

  const handleFundPaymentAccept = () => {
    paymentCheckProcess.current = true;

    if (paymentCheckModal.current) {
      paymentCheckModalInstance.current.toggle();
    }

    verifyUserPaymentChoice();
  };

  const handleFundPaymentDecline = () => {
    paymentCheckProcess.current = false;

    if (paymentCheckModal.current) {
      paymentCheckModalInstance.current.toggle();
    }

    verifyUserPaymentChoice();
  };

  const functionsPassed = {
    separate: separateDecimal,
    commas: numberWithCommas,
  };

  const resetAllData = () => {
    setPurchaseData(() => ({
      fund_units: "",
      total_amount: "",
    }));

    setFundPaymentData(() => ({
      bank_account: "",
      bank_name: "",
      branch_name: "",
      account_title: "",
      iban: "",
      payment_method: "",
      payment_gateway: "raast",
      payment_instrument: "",
      instrument_no: "",
      instrument_date: new Date().toISOString().split("T")[0],
      comment: "",
      payment_proof_file: null,
    }));

    setCollapsedText(true);

    setSelectFund(() => ({
      category: { name: "All" },
      fund: null,
      search: "",
      fund_units: "",
      total_amount: "",
    }));
  };

  useEffect(() => {
    const resetPurchaseModal = () => {
      if (purchaseProcess.current !== "payment" && purchaseProcess.current !== "back") {
        setPurchaseData(() => ({
          fund_units: "",
          total_amount: "",
        }));

        // setSelectFund(() => ({
        //   category: { name: "All" },
        //   fund: null,
        //   search: "",
        // }));

        setActivePurchaseFund(null);
      }
    };

    const resetPurchasePaymentModal = () => {
      purchaseFromCalculator.current = false;
      if (purchaseProcess.current !== "final" && purchaseProcess.current !== "back") {
        setPurchaseData(() => ({
          fund_units: "",
          total_amount: "",
        }));

        setFundPaymentData(() => ({
          bank_account: "",
          bank_name: "",
          branch_name: "",
          account_title: "",
          iban: "",
          payment_method: "",
          payment_gateway: "raast",
          payment_instrument: "",
          instrument_no: "",
          instrument_date: new Date().toISOString().split("T")[0],
          comment: "",
          payment_proof_file: null,
        }));

        setActivePurchaseFund(null);
      }

      if (!backButtonPress.current) {
        backButtonPress.current = false;
        setCollapsedText(true);

        setSelectFund(() => {
          const currentFund = derivedFundsRef.current && derivedFundsRef.current?.length !== 0 ? derivedFundsRef.current?.[0] : null;

          if (currentFund) {
            const amount = 1000;
            const units = amount / parseFloat(currentFund?.navData?.offer_price);

            return {
              category: { name: "All" },
              search: "",
              fund: currentFund,
              total_amount: String(amount),
              fund_units: Number.isFinite(units) ? units : format4(0),
            };
          }

          return {
            category: { name: "All" },
            fund: null,
            search: "",
            fund_units: "",
            total_amount: "",
          };
        });
      }

      // purchaseProcess.current = '';
    };

    const resetPaymentProofModal = () => {
      if (!modalCloseRequest.current) {
        sendRequestOnModalClose();
      }

      resetAllData();

      setActivePurchaseFund(null);
    };

    const resetCalculatorModal = () => {

      backButtonPress.current = false;

      if (purchaseFromCalculator.current) return;
      // setSelectFund(() => ({

      //   category: { name: "All" },
      //   fund: null,
      //   search: "",
      //   fund_units: "",
      //   total_amount: "",
      // }));

      setCollapsedText(true);

      setSelectFund(() => {
        const currentFund = derivedFundsRef.current && derivedFundsRef.current?.length !== 0 ? derivedFundsRef.current?.[0] : null;

        if (currentFund) {
          const amount = 1000;
          const units = amount / parseFloat(currentFund?.navData?.offer_price);

          return {
            category: { name: "All" },
            search: "",
            fund: currentFund,
            total_amount: String(amount),
            fund_units: Number.isFinite(units) ? units : format4(0),
          };
        }

        return {
          category: { name: "All" },
          fund: null,
          search: "",
          fund_units: "",
          total_amount: "",
        };
      });
    };

    if (paymentProofModal.current) {
      paymentProofModalInstance.current = new Modal(paymentProofModal.current, { backdrop: true, keyboard: false });

      paymentProofModal.current.addEventListener("hidden.bs.modal", resetPaymentProofModal);
    }

    if (paymentModal.current) {
      paymentModalInstance.current = new Modal(paymentModal.current, { backdrop: true, keyboard: false });

      paymentModal.current.addEventListener("hidden.bs.modal", resetPurchasePaymentModal);
    }

    if (paymentCheckModal.current) {
      paymentCheckModalInstance.current = new Modal(paymentCheckModal.current, { backdrop: true, keyboard: false });
    }

    if (fundsModalRef.current) {
      modalRef.current = new Modal(fundsModalRef.current, { backdrop: true, keyboard: false });

      fundsModalRef.current.addEventListener("hidden.bs.modal", resetPurchaseModal);
    }

    if (helpModal.current) {
      helpModalInstance.current = new Modal(helpModal.current, { backdrop: true, keyboard: true });

      helpModal.current.addEventListener("hidden.bs.modal", resetCalculatorModal);
    }

    const resizeScreen = () => {
      setScreenSize(window?.innerWidth);
      if (tabsScrollRef.current) {
        tabsRowScrollHandler();
      }
      if (calculatorFundRef.current) {
        fundScrollHandler();
      }
      if (calculatorCategoryRef.current) {
        categoryScrollHandler();
      }
      if (calculatorAmountRef.current) {
        calculatorAmountHandler();
      }
    };

    resizeScreen();
    if (tabsScrollRef.current) {
      tabsRowScrollHandler();
    }
    if (calculatorFundRef.current) {
      fundScrollHandler();
    }
    if (calculatorCategoryRef.current) {
      categoryScrollHandler();
    }
    if (calculatorAmountRef.current) {
      calculatorAmountHandler();
    }

    const callBothScrollHandlers = () => {
      fundScrollHandler();
      categoryScrollHandler();
    };

    window.addEventListener("resize", resizeScreen);
    tabsScrollRef.current?.addEventListener("scroll", tabsRowScrollHandler);
    calculatorAmountRef.current?.addEventListener("scroll", calculatorAmountHandler);
    calculatorFundRef.current?.addEventListener("scroll", fundScrollHandler);
    calculatorCategoryRef.current?.addEventListener("scroll", categoryScrollHandler);
    helpModal.current?.addEventListener("shown.bs.modal", callBothScrollHandlers);

    // const callAsync = async () => {
    //   try {
    //     setFetchingFunds(true);
    //     // const savedToDb = await updateDatabase();

    //     // await fetchMarketData()

    //     // await setFunds();

    //     // await getFunds(updateFundsData, updateVisibleData);
    //     const fetchFunds = async () => {
    //       const data = await getFundsByAmcCode(user);

    //       await new Promise((res) => setTimeout(() => res(), 1000));

    //       if (!data.error) {
    //         updateFundsData(data.data || []);
    //         updateVisibleData(data.data.slice(0, 20));
    //       }
    //     };

    //     fetchFunds();

    //     // console.log(data);

    //     // updateFundsData(data);
    //     // updateVisibleData(data.slice(0, 20));
    //   } catch (error) {
    //     // handle error
    //   } finally {
    //     setFetchingFunds(false);
    //   }
    // };

    // callAsync();

    return () => {
      window.removeEventListener("resize", resizeScreen);
      if (fundsModalRef.current) {
        fundsModalRef.current.removeEventListener("hidden.bs.modal", resetPurchaseModal);
      }
      if (paymentProofModal.current) {
        paymentProofModal.current.removeEventListener("hidden.bs.modal", resetPaymentProofModal);
      }
      if (paymentProofModal.current) {
        helpModal.current.removeEventListener("hidden.bs.modal", resetCalculatorModal);
      }
      tabsScrollRef.current?.removeEventListener("scroll", tabsRowScrollHandler);
      calculatorAmountRef.current?.removeEventListener("scroll", calculatorAmountHandler);
      calculatorFundRef.current?.removeEventListener("scroll", fundScrollHandler);
      calculatorCategoryRef.current?.removeEventListener("scroll", categoryScrollHandler);
      helpModal.current?.removeEventListener("shown.bs.modal", callBothScrollHandlers);

      helpModalInstance.current?.dispose();
      paymentModalInstance.current?.dispose();
      modalRef.current?.dispose();
      paymentCheckModalInstance.current?.dispose();
      paymentProofModalInstance.current?.dispose();
    };
  }, []);

  useEffect(() => {
    // if (calculatorFundRef.current) {
    fundScrollHandler();
    // }

    // if (calculatorCategoryRef.current) {
    categoryScrollHandler();
    // }

    if (funds && funds?.length !== 0) {
      updateVisibleData(funds.slice(0, 20));
      derivedFundsRef.current = funds;
    }

    setSelectFund((prev) => {
      const currentFund = funds?.[0];

      if (currentFund) {
        const amount = 1000;
        const units = amount / parseFloat(currentFund?.navData?.offer_price);

        return {
          ...prev,
          fund: currentFund,
          total_amount: String(amount),
          fund_units: Number.isFinite(units) ? format4(units) : format4(0),
        };
      }

      return prev;
    });
  }, [funds]);

  useEffect(() => {
    if (funds && funds.length !== 0 && purchaseSearchModal) {
      const currentFund = funds.find((item) => item.symbol_code === purchaseSearchSymbol);

      if (currentFund) {
        toggleFundsModal(currentFund);
        navigate("/invest", { replace: true });
      }
      // console.log(currentFund);
    }
  }, [funds, purchaseSearchModal]);

  useEffect(() => {
    tabsRowScrollHandler();
  }, [fundCategories]);

  useEffect(() => {
    if (fundsContainerRef.current) {
      fundsContainerRef.current.scrollTop = 0;
    }

    if (activeTabItem?.name !== "All Funds") {
      setVisibleData(filteredFunds);
    } else {
      setVisibleData(filteredFunds.slice(0, 20));
    }
  }, [filteredFunds]);

  useEffect(() => {
    dataOnModalClose.current = { ...purchaseData, ...fundPaymentData, activeFund: activePurchaseFund };
  }, [purchaseData, fundPaymentData, activePurchaseFund]);

  useEffect(() => {
    const updateData = async () => {
      setLoading((prev) => ({ ...prev, bankAccounts: true }));
      await new Promise((res) => setTimeout(() => res(), 5000));
      try {
        const response = await getFundBankAccounts({ fund_code: activePurchaseFund?.symbol_code, email: user.data.email });

        if (!response.error) {
          const selectFormat = response.data.map((item) => ({
            value: item,
            label: item.account_code || item.account_title,
          }));

          setActiveFundBankAccounts(selectFormat);
        } else {
          setActiveFundBankAccounts([]);
        }
      } finally {
        setLoading((prev) => ({ ...prev, bankAccounts: false }));
      }
    };

    if (user && activePurchaseFund) {
      if (Object.keys(activePurchaseFund).length !== 0) {
        updateData();

        // setPurchaseData(() => ({
        //     fund_units: format4(investAmount / activePurchaseFund?.navData?.nav),
        //     total_amount: format2(investAmount),
        // }));
      }
    }
  }, [activePurchaseFund]);

  useEffect(() => {
    if (location.pathname?.startsWith('/invest')) {
      setActiveMenuItem('invest')
    }
  }, [location.pathname])

  // useEffect(() => {
  //   if (searchTerm && !fetchingFunds) {
  //     const scrollToElement = () => {
  //       const element = document.getElementById(searchTerm);
  //       if (element) {
  //         element.scrollIntoView({ behavior: "smooth", block: "center" });
  //       } else if (retryScrollCount < 5) {
  //         setTimeout(() => {
  //           setRetryScrollCount((prev) => prev + 1);
  //           scrollToElement();
  //         }, 200);
  //       }
  //     };

  //     setTimeout(scrollToElement, 200);
  //   }
  // }, [searchTerm, fetchingFunds]);

  useEffect(() => {
    if (showSearchSelectFund && selectSearchFundRef.current) {
      selectSearchFundRef.current.focus();
    }
  }, [showSearchSelectFund]);

  // console.log(fundCategories);
  // console.log(visibleData);
  // console.log(filteredFunds);

  const tabMenuItems = [
    {
      id: 1,
      title: t("invest.tabs.all"),
      path: "/invest",
    },
    {
      id: 2,
      title: t("invest.tabs.shariah"),
      path: "/shariah",
    },
    {
      id: 3,
      title: t("invest.tabs.conventional"),
      path: "/conventional",
    },
  ];

  return (
    <div
      className='w-100'
      style={{
        borderRadius: "20px",
        // marginTop: '12px',
        // backgroundColor: theme === "dark" ? "var(--theme-dark-one)" : "var(--theme-light-one)",
        backgroundColor: "var(--theme-bg)",
      }}
    >
      {/*
       *	TODO:   Make the tab menu responsive
       * 	GIVEN:  Large already done
       */}

      {/* -- TABS -- */}
      <div className='row w-100 mx-auto align-items-center justify-content-center position-relative flex-md-nowrap p-2'>
        <div
          className='col-auto p-0 position-relative'
          style={{
            overflow: "hidden",
            maxWidth: screenSize >= 576 ? "65%" : "100%",
            // borderTopLeftRadius: '20px',
            // borderTopRightRadius: '20px'
          }}
        >
          {tabScrollingLeft ? (
            <button
              className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
              onClick={() => handleLeftScroll()}
              style={{
                top: "0px",
                left: "3px",
                color: "var(--theme-light-secondary)",
                padding: "2px",
                background: theme === "dark" ? "rgb(48, 61, 84)" : "rgb(203, 217, 245)",
                boxShadow:
                  theme === "dark"
                    ? "rgb(48, 61, 84) 0px 0px 12px 14px, rgb(48, 61, 84) -13px 0px 4px 8px"
                    : "rgb(203, 217, 245) 0px 0px 12px 14px, rgb(203, 217, 245) -12px 0px 0px 6px",
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={30}
                height={30}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M15 6l-6 6l6 6' />
              </svg>
            </button>
          ) : (
            ""
          )}

          {tabScrollingRight ? (
            <button
              className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
              onClick={() => handleRightScroll()}
              style={{
                top: "0px",
                right: "3px",
                color: "var(--theme-light-secondary)",
                padding: "2px",
                background: theme === "dark" ? "rgb(76, 59, 84)" : "rgb(231, 213, 242)",
                boxShadow:
                  theme === "dark"
                    ? "rgb(76, 59, 84) 0px 0px 12px 14px, rgb(76, 59, 84) 13px 0px 4px 8px"
                    : "rgb(231, 213, 242) 0px 0px 12px 14px, rgb(231, 213, 242) 13px 0px 4px 8px",
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={30}
                height={30}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M9 6l6 6l-6 6' />
              </svg>
            </button>
          ) : (
            ""
          )}

          <div
            className='tabs-row row w-100 mx-auto align-items-center gap-2 flex-nowrap overflow-x-auto mw-100 pe-2'
            ref={tabsScrollRef}
            style={{
              scrollbarWidth: "none",
              // borderTopRightRadius: '20.4px',
              // borderTopLeftRadius: '20.4px'
            }}
          >
            <div className={`col-auto p-0`}>
              {/* <button className={`btn btn-primary border-0 ${currentActiveTab() === tabItem.path ? 'active' : ''}`} to={tabItem.path} type="button" style={{ */}
              <button
                className={`btn ${activeTabItem?.type === "all" ? " active" : ""}`}
                type='button'
                onClick={() => setActiveTabItem({ type: "all", name: "All Funds" })}
                style={{
                  lineHeight: "1",
                  fontSize: "0.8rem",
                  padding: "10px 16px 11px",
                  minWidth: "62px",
                  borderRadius: "99px",
                  ...tabButton(theme),
                }}
              >
                {t("invest.tabs.all")}
              </button>
            </div>
            {fundCategories?.map((item, index) => {
              // const numberOfTabs = tabMenuItems.length;
              // // const isPortfolio = activeMenuItem === 'portfolio';

              // let appliedMargin;

              // if (numberOfTabs === 1) {
              // 	appliedMargin = "ms-auto"
              // 	// } else if (isPortfolio) {
              // 	// 	appliedMargin = "ms-auto"
              // } else {
              // 	if (index === 0) {
              // 		appliedMargin = 'ms-auto';
              // 	} else {
              // 		appliedMargin = 'm-0';
              // 	}
              // }

              return (
                <>
                  <div className={`col-auto p-0`} key={index}>
                    {/* <button className={`btn btn-primary border-0 ${currentActiveTab() === tabItem.path ? 'active' : ''}`} to={tabItem.path} type="button" style={{ */}
                    <button
                      className={`btn ${activeTabItem.name === item.name ? " active" : ""}`}
                      type='button'
                      onClick={() => setActiveTabItem(item)}
                      style={{
                        lineHeight: "1",
                        fontSize: "0.8rem",
                        // border: '1px solid',
                        padding: "10px 16px 11px",
                        minWidth: "62px",
                        borderRadius: "99px",
                        ...tabButton(theme),
                      }}
                    >
                      {item.name}
                    </button>
                  </div>
                </>
              );
            })}
          </div>
        </div>
        {/* {screenSize >= 800 ? (

				) : ''} */}
        <div
          className='col-12 col-md-auto d-inline-flex align-items-center p-0 text-center mt-2 mt-md-0'
          style={{
            position: screenSize >= 768 ? "absolute" : "",
            top: "8px",
            right: "7px",
          }}
        >
          <div
            className='d-inline-block me-auto me-md-3 mt-2 mb-1 my-md-0 ms-auto ms-lg-0'
            style={{
              lineHeight: "1.1",
              fontSize: ".825rem",
              color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
            }}
          >
            <button
              type='button'
              className='btn btn-primary d-flex align-items-center justify-content-center flex-nowrap'
              onClick={() => {
                if (helpModal.current) {
                  helpModalInstance.current.toggle();
                }
              }}
              style={{
                fontSize: "12px",
                lineHeight: "1.1",
                letterSpacing: ".05em",
                paddingInlineEnd: "14px",
                paddingInlineStart: "10px",
                borderRadius: "10px",
                // gap: '4px'
              }}
            >
              <Calculator
                strokeWidth={1.5}
                style={{
                  marginInlineEnd: "-2px",
                }}
              />
              <EllipsisVertical strokeWidth={0.75} color='rgba(255,255,255,.7)' />
              <div style={{ marginInlineStart: "1px" }}>
                {t("invest.investment")}
                <br />
                {t("invest.calculator")}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* -- MAIN CONTENT -- */}
      <div
        className=''
        style={{
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
      >
        <div
          className={`add-custom-scroll${theme === "dark" ? "-dark" : ""} d-flex mx-auto flex-column justify-content-start flex-nowrap`}
          ref={fundsContainerRef}
          style={{
            padding: screenSize <= 575 ? "8px 8px 8px 8px" : "4px 10px 10px 12px",
            // paddingTop: '10px',
            borderBottomLeftRadius: "19px",
            borderBottomRightRadius: "19px",
            rowGap: "8px",
            height:
              screenSize <= 575
                ? "calc(100dvh - 260px)"
                : screenSize <= 991 && screenSize >= 768
                  ? "calc(100dvh - 253px)"
                  : screenSize >= 576 && screenSize <= 767
                    ? "calc(100dvh - 314px)"
                    : "calc(100dvh - 180px)",
            overflowY: "auto",
            width: screenSize >= 576 ? "100%" : "576px",
            overflowX: "auto",
          }}
        >
          {visibleData?.length !== 0 ? (
            <div className='col-12 p-0'>
              <hr
                className='m-0 mx-auto'
                style={{
                  maxWidth: "calc(100% - 10px)",
                  color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.45)`,
                }}
              />
            </div>
          ) : (
            ""
          )}
          {visibleData?.map((item, index) => {
            return (
              <>
                <FundItem
                  key={index}
                  data={item}
                  t={t}
                  // investAmount={investAmount}
                  utils={functionsPassed}
                  toggleModal={toggleFundsModal}
                  showDetails={setShowDetails}
                  ref={visibleData.length === filteredFunds.length ? null : index === visibleData.length - 1 ? lastElementRef : null}
                />
                {index <= visibleData.length && (
                  <div className='col-12 p-0' key={String.fromCharCode(65 + index) + index}>
                    <hr
                      className='m-0 mx-auto'
                      style={{
                        maxWidth: "calc(100% - 10px)",
                        color: `rgba(${theme === "dark" ? "var(--theme-dark-tertiary)" : "var(--theme-light-tertiary)"},0.45)`,
                      }}
                    />
                  </div>
                )}
              </>
            );
          })}

          {loadingMoreData ? (
            <div
              className='d-flex justify-content-center gap-3 py-2'
              style={{
                color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                width: screenSize <= 575 ? "calc(100vw - 30px)" : "",
              }}
            >
              <div
                className=''
                role='status'
                style={{
                  fontSize: "1.14rem",
                }}
              >
                {t("app.loading.funds")}
              </div>
              <div
                className='spinner-border'
                style={{
                  fontSize: "0.8rem",
                  height: "28px",
                  width: "28px",
                }}
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>

      {/* <PurchaseFundModal
        isOpen={activeModalStep === 1}
        fund={activePurchaseFund}
        loading={loading.purchaseFund}
        purchaseData={purchaseData}
        updatePurchaseData={handlePurchaseFundChange}
      /> */}

      {/* -- PURCHASE FUND - MODAL -- */}
      <div className='modal fade' id='fundModal' tabIndex='-1' aria-labelledby='fundModal' aria-hidden='true' ref={fundsModalRef}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: purchaseFundStep !== 0 ? "840px" : "600px",
            margin: screenSize >= 616 ? "auto" : ".5rem",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handlePurchaseFund}
            style={{
              backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <div
              className='modal-header align-items-center justify-content-between position-relative border-0'
              style={{
                backgroundColor: theme === "light" ? "rgb(101, 138, 181)" : "rgb(49, 101, 162)",
                minHeight: "67px",
                padding: screenSize >= 576 ? "1rem .8rem" : ".75rem .65rem",
              }}
            >
              <div
                className='ps-2'
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: screenSize >= 576 ? "1rem" : ".82rem",
                }}
              >
                {t("modals.common.step")} 1<span style={{ margin: "0 .125rem" }}>/</span>3
              </div>

              <h1
                className='modal-title position-absolute start-50 translate-middle-x text-center text-nowrap'
                id='goalModal'
                style={{ color: "rgb(224, 232, 240)", fontSize: screenSize >= 576 ? "1.45rem" : "1.25rem", lineHeight: screenSize >= 576 ? "" : "1.1" }}
              >
                {t("modals.purchase.info.title")}
              </h1>
              <button
                type='button'
                className='btn-close p-0 d-flex align-items-center justify-content-center'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  color: "rgb(255,255,255)",
                  background: "none",
                  height: screenSize >= 576 ? "32px" : "28px",
                  width: screenSize >= 576 ? "32px" : "28px",
                  ["--bs-btn-close-opacity"]: "0.65",
                  ["--bs-btn-close-hover-opacity"]: "0.85",
                }}
                onClick={() => toggleFundsModal()}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height={screenSize >= 576 ? 18 : 16}
                  width={screenSize >= 576 ? 18 : 16}
                  stroke='currentColor'
                  fill='currentColor'
                  viewBox='0 0 16 16'
                >
                  <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
                </svg>
              </button>
            </div>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div className='row w-100 mx-auto gap-3 p-3 pb-2'>
                <div className='col-12 p-0'>
                  <div
                    className='row w-100 mx-auto py-2'
                    style={{
                      minHeight: screenSize >= 576 ? "72px" : "65px",
                    }}
                  >
                    <div className='col-12 p-0 ps-2 text-center'>
                      <div
                        className=''
                        style={{
                          fontSize: screenSize >= 576 ? "2rem" : "1.65rem",
                          lineHeight: "1",
                          fontWeight: "500",
                          color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                          marginTop: "5px",
                        }}
                      >
                        {activePurchaseFund?.symbol_code}
                      </div>
                      <div
                        className=''
                        style={{
                          fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                          fontWeight: "400",
                          lineHeight: "1",
                          color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                          marginTop: "3px",
                        }}
                      >
                        {activePurchaseFund?.fund_name}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-12 p-0'>
                  <hr className='my-2 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                </div>
                <div className='col p-0 pe-1'>
                  <div
                    className='d-flex flex-column align-items-center justify-content-center flex-nowrap mx-auto pb-2'
                    style={{
                      minHeight: "180px",
                      maxWidth: "380px",
                    }}
                  >
                    <div className='w-100 mx-auto'>
                      <div
                        className='text-center mb-2'
                        style={{
                          fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                          color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                          fontWeight: "400",
                          lineHeight: "1",
                        }}
                      >
                        {t("modals.purchase.info.amount")}
                      </div>
                <div
                  className='w-100 d-flex align-items-center flex-nowrap overflow-auto gap-2 px-3 mb-2'
                  style={{
                    scrollbarWidth: "none",
                  }}
                >
                  {["100000", "500000", "1000000", "5000000"].map((num, idx) => {
                    return (
                      <button
                        className={`btn${parseFloat(purchaseData?.total_amount) === parseFloat(num) ? " active" : ""}`}
                        value={num}
                        key={idx}
                        name='total_amount'
                        onClick={(e) => handlePurchaseFundChange({ target: { name: 'total_amount', value: num }})}
                        type='button'
                        style={{
                          fontSize: "13px",
                          paddingBlock: "3px",
                          fontWeight: "300",
                          lineHeight: "1.25",
                          flex: "1",
                          borderRadius: "999px",
                          ...tabButton(theme),
                        }}
                      >
                        {numberWithCommas(num)}
                      </button>
                    );
                  })}
                </div>
                      <input
                        type='text'
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                        name='total_amount'
                        value={purchaseData.total_amount && numberWithCommas(purchaseData.total_amount)}
                        style={{
                          ...themeBasedPurchaseFormControl(theme),
                          marginTop: ".4rem",
                          fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                        }}
                        onChange={handlePurchaseFundChange}
                      />
                    </div>
                    <div
                      className='text-center mt-2'
                      style={{
                        fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                        minHeight: "14px",
                        color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                        fontWeight: "400",
                        lineHeight: "1",
                      }}
                    >
                      {purchaseData.total_amount ? amountToWords(purchaseData.total_amount) : ""}
                    </div>
                    <div className='row w-100 mx-auto mt-3'>
                      <div className='col p-0 pe-1'>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("app.fund.price")}
                        </div>
                        <input
                          type='text'
                          disabled
                          className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                          value={format4(activePurchaseFund?.navData?.offer_price || 0)}
                          style={{
                            ...themeBasedPurchaseFormControl(theme),
                            marginTop: ".4rem",
                            fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                          }}
                        />
                      </div>
                      <div className='col-auto p-0'>
                        <div className='d-flex flex-column justify-content-end align-items-center h-100 pb-2'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={26}
                            height={26}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='var(--theme-light-primary)'
                            strokeWidth={1.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-x'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M18 6l-12 12' />
                            <path d='M6 6l12 12' />
                          </svg>
                        </div>
                      </div>
                      <div className='col p-0 ps-1'>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("modals.purchase.info.units")}
                        </div>
                        <input
                          type='text'
                          name='fund_units'
                          value={purchaseData.fund_units && numberWithCommas(purchaseData.fund_units)}
                          className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                          style={{
                            ...themeBasedPurchaseFormControl(theme),
                            marginTop: ".4rem",
                            fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                          }}
                          onChange={handlePurchaseFundChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='modal-footer border-0 flex-nowrap justify-content-between'>
              <div
                className='pe-1'
                style={{
                  maxWidth: screenSize >= 576 ? "calc(100% - 300px)" : "65%",
                  fontSize: screenSize >= 576 ? ".775rem" : ".75rem",
                  lineHeight: "1.2",
                  color: theme === "light" ? "rgba(0,0,0,.6)" : "rgba(255,255,255,.5)",
                }}
              >
                {t("modals.purchase.info.caption")}
              </div>
              <button
                type='submit'
                disabled={loading.purchaseFund}
                className='btn btn-primary border-0 px-4 pb-2 d-flex align-items-center justify-content-center'
                style={{
                  minHeight: "38px",
                  minWidth: "80px",
                  fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                }}
              >
                {loading.purchaseFund ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: screenSize >= 576 ? "15px" : "14px",
                        width: screenSize >= 576 ? "15px" : "14px",
                      }}
                    ></span>
                    {/* <span role="status">Loading...</span> */}
                  </>
                ) : (
                  t("modals.common.next")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* -- PAYMENT FUND - MODAL -- */}
      <div className='modal fade' id='paymentModal' tabIndex='-1' aria-labelledby='paymentModal' aria-hidden='true' ref={paymentModal}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "700px",
            marginInline: screenSize >= 655 ? "auto" : ".5rem",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handleFundPayment}
            style={{
              backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <div
              className='modal-header align-items-center justify-content-between position-relative border-0'
              style={{
                backgroundColor: theme === "light" ? "rgb(101, 138, 181)" : "rgb(49, 101, 162)",
                minHeight: "67px",
                padding: screenSize >= 576 ? "1rem .8rem" : ".75rem .65rem",
              }}
            >
              <div
                className='ps-2'
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: screenSize >= 576 ? "1rem" : ".82rem",
                }}
              >
                {t("modals.common.step")} 2<span style={{ margin: "0 .125rem" }}>/</span>3
              </div>

              <h1
                className='modal-title position-absolute start-50 translate-middle-x text-center text-nowrap'
                id='goalModal'
                style={{ color: "rgb(224, 232, 240)", fontSize: screenSize >= 576 ? "1.45rem" : "1.25rem", lineHeight: screenSize >= 576 ? "" : "1.1" }}
              >
                {t("modals.purchase.payment.title")}
              </h1>

              <button
                type='button'
                className='btn-close p-0 d-flex align-items-center justify-content-center'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  color: "rgb(255,255,255)",
                  background: "none",
                  height: screenSize >= 576 ? "32px" : "28px",
                  width: screenSize >= 576 ? "32px" : "28px",
                  ["--bs-btn-close-opacity"]: "0.65",
                  ["--bs-btn-close-hover-opacity"]: "0.85",
                }}
                onClick={togglePaymentModal}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height={screenSize >= 576 ? 18 : 16}
                  width={screenSize >= 576 ? 18 : 16}
                  stroke='currentColor'
                  fill='currentColor'
                  viewBox='0 0 16 16'
                >
                  <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
                </svg>
              </button>
            </div>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div className='row w-100 mx-auto p-2 px-2 p-sm-3 px-sm-3 pb-sm-2'>
                <div className='col-12 p-0'>
                  <div className='row w-100 mx-auto py-3 pb-4 px-1 row-gap-3'>
                    {/* FUND INFORMATION */}
                    <div className='col-12 p-0'>
                      <div className='row w-100 mx-auto justify-content-center align-items-center flex-sm-nowrap gap-sm-0 gap-3'>
                        <div className='col-12 col-sm-4 p-0'>
                          <div className='d-flex flex-column align-items-center justify-content-center'>
                            <div
                              className=''
                              style={{
                                fontSize: ".75rem",
                                fontWeight: "400",
                                letterSpacing: ".03em",
                                color: "rgb(170, 170, 170)",
                              }}
                            >
                              {t("modals.purchase.payment.fund")}
                            </div>
                            <div
                              className=''
                              style={{
                                fontSize: "1.55rem",
                                lineHeight: "1",
                                fontWeight: "500",
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                                marginTop: "5px",
                              }}
                            >
                              {activePurchaseFund?.symbol_code}
                            </div>
                            <div
                              className='text-truncate'
                              data-tooltip-id={"tt-simple"}
                              data-tooltip-content={activePurchaseFund?.fund_name}
                              style={{
                                fontSize: ".75rem",
                                fontWeight: "400",
                                lineHeight: "1",
                                color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                                marginTop: "3px",
                                maxWidth: "140px",
                                cursor: "pointer",
                              }}
                            >
                              {activePurchaseFund?.fund_name}
                            </div>

                            <Tooltip
                              // anchorSelect={"asd"}
                              id={"tt-simple"}
                              place='bottom'
                              // offset={12}
                              // delayShow={120}
                              className='my-rt'
                              classNameArrow='my-rt-arrow'
                              border='1px solid rgba(255,255,255,0.12)'
                              arrowSize={8}
                              opacity={0.95}
                            />
                          </div>
                        </div>
                        <div className='col-12 col-sm-4 p-0'>
                          <div className='d-flex flex-column align-items-center justify-content-center'>
                            <div
                              className=''
                              style={{
                                fontSize: ".75rem",
                                fontWeight: "400",
                                letterSpacing: ".03em",
                                color: "rgb(170, 170, 170)",
                              }}
                            >
                              {t("modals.purchase.info.amount")}
                            </div>
                            <div
                              className=''
                              dir='ltr'
                              style={{
                                fontSize: "1.55rem",
                                lineHeight: "1",
                                fontWeight: "500",
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                                marginTop: "5px",
                              }}
                            >
                              <span
                                className=''
                                dir='ltr'
                                style={{ fontSize: ".9rem", marginRight: ".25rem", color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)" }}
                              >
                                {t("app.fund.rupee")}
                              </span>
                              {(purchaseData?.total_amount ? numberWithCommas(separateDecimal(purchaseData?.total_amount).whole) : "0") + "."}
                              <span className='me-1' style={{ fontSize: ".9rem", color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)" }}>
                                {purchaseData?.total_amount ? separateDecimal(format4(purchaseData?.total_amount)).decimal : "0000"}
                              </span>
                            </div>
                            <div
                              className=''
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "400",
                                lineHeight: "1",
                                height: "12px",
                                color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                                marginTop: "3px",
                              }}
                            ></div>
                          </div>
                        </div>
                        <div className='col-12 col-sm-4 p-0'>
                          <div className='d-flex flex-column align-items-center justify-content-center'>
                            <div
                              className=''
                              style={{
                                fontSize: ".75rem",
                                fontWeight: "400",
                                letterSpacing: ".03em",
                                color: "rgb(170, 170, 170)",
                              }}
                            >
                              {t("modals.purchase.info.units")}
                            </div>
                            <div
                              className=''
                              style={{
                                fontSize: "1.55rem",
                                lineHeight: "1",
                                fontWeight: "500",
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                                marginTop: "5px",
                              }}
                            >
                              {/* <span className='me-1' style={{ fontSize: ".9rem", color: "rgb(111, 151, 200)" }}>
                                Rs
                              </span> */}
                              <FormattedNumber number={purchaseData.fund_units} />
                            </div>
                            <div
                              className=''
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "400",
                                lineHeight: "1",
                                height: "12px",
                                color: theme === "dark" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                                marginTop: "3px",
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* <div className='col-12 p-0'>
                                            <label
                                                htmlFor='bank-name'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                Select Bank Account <sup style={{ color: "rgb(239, 112, 112)", fontSize: "15px", top: "-.25em" }}>*</sup>
                                            </label>
                                            <Select
                                                className='basic-single'
                                                classNamePrefix='select'
                                                name='bank_account'
                                                defaultValue={""}
                                                value={fundPaymentData.bank_account}
                                                onChange={updateFundPaymentData}
                                                // getOptionLabel={option }
                                                isDisabled={false}
                                                isLoading={false}
                                                isClearable={true}
                                                isRtl={false}
                                                isSearchable={true}
                                                options={activeFundBankAccounts || []}
                                                styles={{
                                                    multiValue: (provided) => ({
                                                        ...provided,
                                                    }),
                                                    singleValue: (provided) => ({
                                                        ...provided,
                                                        color: theme === "light" ? "#212529" : "rgba(240, 240, 240, .95)",
                                                    }),
                                                    clearIndicator: (provided, state) => ({
                                                        ...provided,
                                                        color: state.isFocused
                                                            ? theme === "dark"
                                                                ? "rgb(200,200,200)"
                                                                : "rgb(144, 144, 144)"
                                                            : theme === "dark"
                                                              ? "rgba(240, 240, 240, .65)"
                                                              : "rgb(184, 184, 184)",
                                                        "&:hover": {
                                                            color: state.isFocused
                                                                ? theme === "dark"
                                                                    ? "rgb(240,240,240)"
                                                                    : "rgb(105, 105, 105)"
                                                                : theme === "dark"
                                                                  ? "rgba(240, 240, 240, .85)"
                                                                  : "rgb(153, 153, 153)",
                                                        },
                                                    }),
                                                    menu: (provided) => ({
                                                        ...provided,
                                                        fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                        backgroundColor: theme === "light" ? "rgb(230, 230, 230)" : "rgb(70, 70, 78)",
                                                    }),
                                                    control: (provided, state) => ({
                                                        ...provided,
                                                        minHeight: screenSize >= 576 ? "40px" : "38px",
                                                        // width: '120px',
                                                        // maxWidth: '120px',
                                                        fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                        backgroundColor: theme === "light" ? "rgb(230, 230, 230)" : "rgb(70, 70, 78)",
                                                        borderColor: theme === "light" ? "rgb(197, 211, 230)" : "rgb(141, 136, 143)",
                                                        color: theme === "light" ? "#212529" : "rgba(240, 240, 240, .95)",
                                                        borderRadius: ".375rem",
                                                        fontFamily: "inherit",
                                                        boxShadow: state.isFocused
                                                            ? `0 0 2px 1px ${theme === "dark" ? "rgba(136, 136, 136, 0.5)" : "rgba(136, 136, 136, 0.5)"}` // e.g. a semi-transparent red ring
                                                            : "none",
                                                        "&:hover": {
                                                            borderColor: theme === "light" ? "rgb(156, 155, 156)" : "rgb(174, 172, 175)",
                                                        },
                                                    }),
                                                    input: (provided) => ({
                                                        ...provided,
                                                        color: theme === "light" ? "#212529" : "rgba(240, 240, 240, .95)",
                                                    }),
                                                    dropdownIndicator: (provided, state) => ({
                                                        ...provided,
                                                        transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : undefined,
                                                        transition: "all 0.2s ease",
                                                        color: state.isFocused
                                                            ? theme === "dark"
                                                                ? "rgb(200,200,200)"
                                                                : "rgb(144, 144, 144)"
                                                            : theme === "dark"
                                                              ? "rgba(240, 240, 240, .65)"
                                                              : "rgb(184, 184, 184)",
                                                        "&:hover": {
                                                            color: state.isFocused
                                                                ? theme === "dark"
                                                                    ? "rgb(240,240,240)"
                                                                    : "rgb(105, 105, 105)"
                                                                : theme === "dark"
                                                                  ? "rgba(240, 240, 240, .85)"
                                                                  : "rgb(153, 153, 153)",
                                                        },
                                                    }),
                                                    placeholder: (provided) => ({
                                                        ...provided,
                                                        color: theme === "light" ? "rgba(0, 0, 0, 0.4)" : "rgba(240, 240, 240, .65)",
                                                    }),
                                                    menuList: (provided) => ({
                                                        ...provided,
                                                        // backgroundColor: theme === 'dark' ? "rgb(230, 230, 230)" : "rgb(70, 70, 78)",
                                                        borderRadius: 4,
                                                        padding: 0,
                                                    }),
                                                    option: (provided, state) => ({
                                                        ...provided,
                                                        color: state.isSelected
                                                            ? theme === "dark"
                                                                ? "rgb(250,250,250)"
                                                                : "rgb(240,240,240)"
                                                            : theme === "dark"
                                                              ? "rgba(240, 240, 240, .95)"
                                                              : "rgb(33,37,41)",
                                                        backgroundColor: state.isSelected
                                                            ? theme === "dark"
                                                                ? "rgb(203, 110, 209)"
                                                                : "rgb(44, 106, 182)"
                                                            : theme === "dark"
                                                              ? "rgb(70, 70, 78)"
                                                              : "rgb(230, 230, 230)",
                                                        fontWeight: "400",
                                                        "&:hover": {
                                                            cursor: "pointer",
                                                            backgroundColor: state.isSelected ? "" : theme === "dark" ? "rgb(230, 230, 230)" : "rgb(200, 200, 200)",
                                                            color: state.isSelected ? "" : theme === "dark" ? "rgb(33,37,41)" : "",
                                                        },
                                                    }),
                                                }}
                                            />
                                        </div>
                                        <div className='col-12 col-sm p-0 pe-sm-3'>
                                            <label
                                                htmlFor='bank-name'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                Bank Name
                                            </label>
                                            <input
                                                type='text'
                                                name='bank_name'
                                                value={fundPaymentData.bank_name}
                                                disabled={true}
                                                onChange={updateFundPaymentData}
                                                className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                                                id='portfolio-name'
                                                style={{
                                                    ...themeBasedPurchaseFormControl(theme),
                                                    fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                    // backgroundColor: 'rgb(238, 242, 246)',
                                                }}
                                            />
                                        </div>
                                        <div className='col-12 col-sm p-0 ps-sm-3'>
                                            <label
                                                htmlFor='branch_name'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                Branch Name
                                            </label>
                                            <input
                                                type='text'
                                                name='branch_name'
                                                value={fundPaymentData.branch_name}
                                                disabled={true}
                                                onChange={updateFundPaymentData}
                                                className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                                                id='portfolio-name'
                                                style={{
                                                    ...themeBasedPurchaseFormControl(theme),
                                                    fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                    // backgroundColor: 'rgb(238, 242, 246)',
                                                }}
                                            />
                                        </div>
                                        <div className='col-12 p-0'>
                                            <label
                                                htmlFor='account_title'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                Account Title
                                            </label>
                                            <input
                                                type='text'
                                                name='account_title'
                                                value={fundPaymentData.account_title}
                                                disabled={true}
                                                onChange={updateFundPaymentData}
                                                className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                                                id='portfolio-name'
                                                style={{
                                                    ...themeBasedPurchaseFormControl(theme),
                                                    fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                    // backgroundColor: 'rgb(238, 242, 246)',
                                                }}
                                            />
                                        </div>
                                        <div className='col-12 p-0'>
                                            <label
                                                htmlFor='iban'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                IBAN No.
                                            </label>
                                            <input
                                                type='text'
                                                name='iban'
                                                value={fundPaymentData.iban}
                                                onChange={updateFundPaymentData}
                                                disabled={true}
                                                className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                                                id='portfolio-name'
                                                style={{
                                                    ...themeBasedPurchaseFormControl(theme),
                                                    fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                    // backgroundColor: 'rgb(238, 242, 246)',
                                                }}
                                            />
                                        </div>
                                        <div className='col-12 col-sm p-0 pe-sm-3'>
                                            <label
                                                htmlFor='amount'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                Amount
                                            </label>
                                            <input
                                                type='text'
                                                name='amount'
                                                value={purchaseData.total_amount ? numberWithCommas(purchaseData.total_amount) : ""}
                                                disabled={true}
                                                className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                                                id='portfolio-name'
                                                style={{
                                                    ...themeBasedPurchaseFormControl(theme),
                                                    fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                    // backgroundColor: 'rgb(238, 242, 246)',
                                                }}
                                            />
                                        </div>
                                        <div className='col-12 col-sm p-0 ps-sm-3'>
                                            <label
                                                htmlFor='fund_units'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                Units
                                            </label>
                                            <input
                                                type='text'
                                                name='fund_units'
                                                value={purchaseData.fund_units ? numberWithCommas(purchaseData.fund_units) : ""}
                                                disabled={true}
                                                className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                                                id='portfolio-name'
                                                style={{
                                                    ...themeBasedPurchaseFormControl(theme),
                                                    fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                    // backgroundColor: 'rgb(238, 242, 246)',
                                                }}
                                            />
                                        </div>
                                        {fundPaymentData.bank_account !== "" ? (
                                            <div className='col-12 p-0 mt-2'>
                                                <div
                                                    className='ps-1'
                                                    style={{
                                                        color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                        fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                        fontWeight: "400",
                                                        lineHeight: "1.1",
                                                        marginBottom: ".5rem",
                                                    }}
                                                >
                                                    Payment Method
                                                </div>
                                                <div
                                                    className='d-flex align-items-center justify-content-evenly'
                                                    style={{
                                                        minHeight: "40px",
                                                        color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.7)",
                                                    }}
                                                >
                                                    <div className='form-check'>
                                                        <input
                                                            className='form-check-input'
                                                            type='radio'
                                                            name='payment_method'
                                                            id='gender_male'
                                                            value='IBFT'
                                                            checked={fundPaymentData.payment_method === "IBFT"}
                                                            onChange={updateFundPaymentData}
                                                        />
                                                        <label
                                                            className='form-check-label'
                                                            for='gender_male'
                                                            style={{
                                                                fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                            }}
                                                        >
                                                            IBFT
                                                        </label>
                                                    </div>
                                                    <div className='form-check'>
                                                        <input
                                                            className='form-check-input'
                                                            type='radio'
                                                            name='payment_method'
                                                            id='gender_female'
                                                            value='Deposit'
                                                            checked={fundPaymentData.payment_method === "Deposit"}
                                                            onChange={updateFundPaymentData}
                                                        />
                                                        <label
                                                            className='form-check-label'
                                                            for='gender_female'
                                                            style={{
                                                                fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                            }}
                                                        >
                                                            Deposit
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            ""
                                        )}
                                        <div className='col-12 p-0'>
                                            <label
                                                htmlFor='comment'
                                                className='form-label ps-1'
                                                style={{
                                                    color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                                                    fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                                                    fontWeight: "400",
                                                    lineHeight: "1.1",
                                                    marginBottom: ".5rem",
                                                }}
                                            >
                                                Comment
                                            </label>
                                            <textarea
                                                type='text'
                                                name='comment'
                                                value={fundPaymentData.comment}
                                                onChange={updateFundPaymentData}
                                                className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                                                id='portfolio-name'
                                                style={{
                                                    ...themeBasedPurchaseFormControl(theme),
                                                    fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                                                    // backgroundColor: 'rgb(238, 242, 246)',
                                                }}
                                            />
                                        </div> */}
                  </div>
                </div>
                <div className='col-12 p-0'>
                  <hr className='mt-2 mb-0 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                </div>
                <div className='col-12 p-0'>
                  <div
                    className='row w-100 mx-auto'
                    style={{
                      minHeight: "200px",
                    }}
                  >
                    <div className='col-12 col-sm-auto p-0 px-3 px-sm-0'>
                      <div
                        className='d-flex flex-column align-items-center'
                        style={{
                          rowGap: ".85rem",
                          padding: screenSize >= 576 ? ".8rem .8rem" : "1.5rem .75rem",
                        }}
                      >
                        <button
                          className={`payment-option-btn${theme === "dark" ? "-dark" : ""}`}
                          type='button'
                          style={{
                            width: screenSize >= 576 ? "142px" : "100%",
                            color: fundPaymentData.payment_gateway === "raast" ? (theme === "dark" ? "" : "rgb(236, 236, 239)") : "",
                            backgroundColor: fundPaymentData.payment_gateway === "raast" ? (theme === "dark" ? "#000" : "rgb(62 64 105)") : "",
                            borderColor: fundPaymentData.payment_gateway === "raast" ? (theme === "dark" ? "rgba(203, 110, 209,.7)" : "rgb(62 64 105)") : "",
                          }}
                          onClick={() => setFundPaymentData((prev) => ({ ...prev, payment_gateway: "raast" }))}
                        >
                          <div className='svg-wrapper-1'>
                            <div className='svg-wrapper'>
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width={26}
                                height={26}
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={2}
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-torii'
                                style={
                                  {
                                    // color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(178, 54, 187)",
                                  }
                                }
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M4 4c5.333 1.333 10.667 1.333 16 0' />
                                <path d='M4 8h16' />
                                <path d='M12 5v3' />
                                <path d='M18 4.5v15.5' />
                                <path d='M6 4.5v15.5' />
                              </svg>
                            </div>
                          </div>
                          <span className='text-start ms-4 ms-sm-2'>
                            {t("modals.purchase.payment.raast.A")}
                            {screenSize >= 576 ? <br /> : " "}
                            {t("modals.purchase.payment.raast.B")}
                          </span>
                        </button>
                        <button
                          className={`payment-option-btn${theme === "dark" ? "-dark" : ""}`}
                          type='button'
                          style={{
                            width: screenSize >= 576 ? "142px" : "100%",
                            color: fundPaymentData.payment_gateway === "bank" ? (theme === "dark" ? "" : "rgb(236, 236, 239)") : "",
                            backgroundColor: fundPaymentData.payment_gateway === "bank" ? (theme === "dark" ? "#000" : "rgb(61 64 98)") : "",
                            borderColor: fundPaymentData.payment_gateway === "bank" ? (theme === "dark" ? "rgba(203, 110, 209,.7)" : "rgb(57 60 89)") : "",
                          }}
                          onClick={() => setFundPaymentData((prev) => ({ ...prev, payment_gateway: "bank" }))}
                        >
                          <div className='svg-wrapper-1'>
                            <div className='svg-wrapper'>
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width={26}
                                height={26}
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={1.5}
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-building-bank'
                                style={
                                  {
                                    // color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(216, 22, 230)",
                                  }
                                }
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M3 21l18 0' />
                                <path d='M3 10l18 0' />
                                <path d='M5 6l7 -3l7 3' />
                                <path d='M4 10l0 11' />
                                <path d='M20 10l0 11' />
                                <path d='M8 14l0 3' />
                                <path d='M12 14l0 3' />
                                <path d='M16 14l0 3' />
                              </svg>
                            </div>
                          </div>
                          <span className='text-start ms-4 ms-sm-2'>
                            {t("modals.purchase.payment.bank.A")}
                            {screenSize >= 576 ? <br /> : " "}
                            {t("modals.purchase.payment.bank.B")}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className='col-12 col-sm-auto p-0'>
                      {screenSize >= 576 ? (
                        <div
                          style={{
                            height: "100%",
                            width: "1px",
                            backgroundColor: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)",
                            opacity: ".25",
                            // backgroundColor: "rgba(255,255,255,.8)",
                          }}
                        ></div>
                      ) : (
                        <div
                          style={{
                            height: "1px",
                            width: "100%",
                            backgroundColor: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)",
                            opacity: ".25",
                            // backgroundColor: "rgba(255,255,255,.8)",
                          }}
                        ></div>
                      )}
                    </div>
                    <div className='col p-0'>
                      <div
                        className='mb-2 mb-sm-0 text-center text-sm-start'
                        style={{
                          fontSize: ".85rem",
                          lineHeight: "1.2",
                          color: "rgb(168, 168, 168)",
                          padding: screenSize >= 576 ? ".8rem .8rem .8rem 1.3rem" : ".8rem .6rem .8rem .8rem",
                          // paddingBottom: ".2rem",
                        }}
                      >
                        {fundPaymentData.payment_gateway === "raast" ? (
                          <>
                            <span>{t("modals.purchase.payment.message.transfer.A")}</span>
                            <span style={{ color: "var(--theme-light-secondary)", fontSize: ".95rem" }}>{numberWithCommas(purchaseData.total_amount)}</span>
                            <span>{t("modals.purchase.payment.message.transfer.B")}</span>
                          </>
                        ) : (
                          <>
                            <span>{t("modals.purchase.payment.message.deposit.A")}</span>
                            <span style={{ color: "var(--theme-light-secondary)", fontSize: ".95rem" }}>{numberWithCommas(purchaseData.total_amount)}</span>
                            <span>{t("modals.purchase.payment.message.deposit.B")}</span>
                          </>
                        )}
                      </div>
                      <div
                        className={`d-flex flex-column position-relative flex-nowrap add-custom-scroll-dashboard${theme === "dark" ? "-dark" : ""}`}
                        style={{
                          minHeight: screenSize >= 576 ? "340px" : "140px",
                          maxHeight: screenSize >= 576 ? "340px" : "140px",
                          overflowY: "auto",
                          rowGap: ".85rem",
                          padding: screenSize >= 576 ? "0 .8rem .6rem 20px" : "0 .1rem .6rem .8rem",
                          scrollbarWidth: screenSize >= 576 ? "" : "none",
                        }}
                      >
                        {loading.bankAccounts ? (
                          <div
                            className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
                            style={{
                              color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
                            }}
                          >
                            {/* <div
                                className=''
                                role='status'
                                style={{
                                  fontSize: "1rem",
                                }}
                              >
                                Loading Bank Accounts ...
                              </div> */}
                            <div
                              className='spinner-border'
                              style={{
                                fontSize: "0.8rem",
                                height: "36px",
                                width: "36px",
                                color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                              }}
                            >
                              <span className='visually-hidden'>Loading...</span>
                            </div>
                          </div>
                        ) : activeFundBankAccounts?.length !== 0 ? (
                          activeFundBankAccounts?.map((acct, index) => (
                            <BankAccountCard
                              key={index}
                              account={acct}
                              selected={acct.value.account_code === fundPaymentData.bank_account.account_code}
                              onSelect={updateFundPaymentData}
                            />
                          ))
                        ) : (
                          <div
                            className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
                            style={{
                              color: theme === "dark" ? "rgb(180,180,180)" : "rgb(35, 35, 35)",
                            }}
                          >
                            <div
                              className=''
                              role='status'
                              style={{
                                fontSize: ".9rem",
                                fontWeight: "500",
                              }}
                            >
                              No Bank Accounts Found
                            </div>
                          </div>
                        )}

                        {/* {selectedId && (
                                                    <div className='mt-3'>
                                                        <strong>Selected Account:</strong> {accounts.find((a) => a.id === selectedId).bankName} -{" "}
                                                        {accounts.find((a) => a.id === selectedId).accountNumber}
                                                    </div>
                                                )} */}
                      </div>
                      {fundPaymentData.payment_gateway === "bank"
                        ? ""
                        : // <>
                          //   <div
                          //     className='d-block mb-2'
                          //     style={{
                          //       height: ".5px",
                          //       backgroundColor: "rgb(134, 151, 168)",
                          //       opacity: ".25",
                          //     }}
                          //   ></div>
                          //   <div
                          //     className='pb-0'
                          //     style={{
                          //       padding: screenSize >= 576 ? ".8rem .8rem" : ".8rem .4rem",
                          //     }}
                          //   >
                          //     <div
                          //       className='ps-1'
                          //       style={{
                          //         color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                          //         fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                          //         fontWeight: "400",
                          //         lineHeight: "1.1",
                          //         marginBottom: ".5rem",
                          //       }}
                          //     >
                          //       {t("modals.purchase.payment.instrument.main")}
                          //     </div>
                          //     <div
                          //       className='d-flex align-items-center justify-content-evenly'
                          //       style={{
                          //         minHeight: "40px",
                          //         color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.7)",
                          //       }}
                          //     >
                          //       <div
                          //         className='form-check'
                          //         style={
                          //           {
                          //             // minHeight: "18px",
                          //           }
                          //         }
                          //       >
                          //         <input
                          //           className='form-check-input'
                          //           type='radio'
                          //           name='payment_instrument'
                          //           id='cheque'
                          //           value='Cheque'
                          //           checked={fundPaymentData.payment_instrument === "Cheque"}
                          //           onChange={updateFundPaymentData}
                          //           style={{
                          //             width: screenSize >= 576 ? "16px" : "14px",
                          //             height: screenSize >= 576 ? "16px" : "14px",
                          //             marginTop: "0",
                          //             // marginTop: screenSize >= 576 ? ".37em" : ".425rem",
                          //           }}
                          //         />
                          //         <label
                          //           className='form-check-label'
                          //           htmlFor='cheque'
                          //           style={
                          //             {
                          //               // fontSize: screenSize >= 576 ? ".875rem" : ".85rem",
                          //               // lineHeight: "1.1",
                          //             }
                          //           }
                          //         >
                          //           {t("modals.purchase.payment.instrument.cheque")}
                          //         </label>
                          //       </div>
                          //       <div className='form-check'>
                          //         <input
                          //           className='form-check-input'
                          //           type='radio'
                          //           name='payment_instrument'
                          //           id='dd'
                          //           value='DD'
                          //           checked={fundPaymentData.payment_instrument === "DD"}
                          //           onChange={updateFundPaymentData}
                          //           style={{
                          //             width: screenSize >= 576 ? "16px" : "14px",
                          //             height: screenSize >= 576 ? "16px" : "14px",
                          //             marginTop: "0",
                          //             // marginTop: screenSize >= 576 ? ".37em" : ".425rem",
                          //           }}
                          //         />
                          //         <label
                          //           className='form-check-label'
                          //           htmlFor='dd'
                          //           style={
                          //             {
                          //               // fontSize: screenSize >= 576 ? ".875rem" : ".85rem",
                          //               // lineHeight: "1.1",
                          //             }
                          //           }
                          //         >
                          //           {t("modals.purchase.payment.instrument.draft")}
                          //         </label>
                          //       </div>
                          //     </div>
                          //   </div>
                          //   <div
                          //     className='row w-100 mx-auto align-items-center'
                          //     style={{
                          //       padding: screenSize >= 576 ? ".8rem" : ".4rem",
                          //       gap: "2rem",
                          //     }}
                          //   >
                          //     <div className='col-12 col-sm p-0'>
                          //       <label
                          //         htmlFor='instrument_no'
                          //         className='form-label ps-1'
                          //         style={{
                          //           color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                          //           fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                          //           fontWeight: "400",
                          //           lineHeight: "1.1",
                          //           marginBottom: ".5rem",
                          //         }}
                          //       >
                          //         {t("modals.purchase.payment.instrument.number")}
                          //       </label>
                          //       <input
                          //         type='text'
                          //         id='instrument_no'
                          //         name='instrument_no'
                          //         value={fundPaymentData.instrument_no}
                          //         onChange={updateFundPaymentData}
                          //         className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""}`}
                          //         style={{
                          //           ...themeBasedPurchaseFormControl(theme),
                          //           fontSize: screenSize >= 576 ? ".95rem" : ".95rem",
                          //           paddingBlock: ".4rem",
                          //           // backgroundColor: 'rgb(238, 242, 246)',
                          //         }}
                          //       />
                          //     </div>
                          //     <div className='col-12 col-sm p-0'>
                          //       <label
                          //         htmlFor='instrument_date'
                          //         className='form-label ps-1'
                          //         style={{
                          //           color: theme === "light" ? "rgba(0,0,0,0.6)" : "rgba(255, 255, 255, 0.8)",
                          //           fontSize: screenSize >= 576 ? ".85rem" : ".78rem",
                          //           fontWeight: "400",
                          //           lineHeight: "1.1",
                          //           marginBottom: ".5rem",
                          //         }}
                          //       >
                          //         {t("modals.purchase.payment.instrument.date")}
                          //       </label>
                          //       <input
                          //         className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} text-start`}
                          //         placeholder=''
                          //         id='instrument_date'
                          //         name='instrument_date'
                          //         value={fundPaymentData.instrument_date}
                          //         onChange={updateFundPaymentData}
                          //         type='date'
                          //         style={{
                          //           ...themeBasedPurchaseFormControl(theme),
                          //           // ...themeBasedSignupFormControl(theme),
                          //           // ["--bs-secondary-bg"]: "rgb(209, 209, 209)",
                          //           fontSize: screenSize >= 576 ? ".95rem" : ".95rem",
                          //           paddingBlock: ".4rem",
                          //         }}
                          //       />
                          //     </div>
                          //   </div>
                          // </>
                          ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='modal-footer border-0 flex-nowrap flex-column flex-sm-row justify-content-between pt-0 pb-3 pb-sm-2 pt-sm-2 gap-3 gap-sm-0'>
              <div
                className='px-2 px-sm-0 pe-sm-1 text-center text-sm-start'
                style={{
                  maxWidth: screenSize >= 576 ? "calc(100% - 300px)" : "100%",
                  fontSize: screenSize >= 576 ? ".775rem" : ".75rem",
                  lineHeight: "1.2",
                  color: theme === "light" ? "" : "rgba(255,255,255,.5)",
                }}
              >
                {t("modals.purchase.payment.caption")}
              </div>
              <div className='d-inline-flex align-items-center justify-content-end flex-nowrap gap-3'>
                <button
                  type='button'
                  // disabled={loading.paymentFund}
                  className='btn btn-primary border-0 px-4 pb-2 d-flex align-items-center justify-content-center'
                  onClick={handlePaymentBackButton}
                  style={{
                    minHeight: "38px",
                    minWidth: "82px",
                    fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                  }}
                >
                  {t("modals.common.back")}
                </button>
                <button
                  type='submit'
                  disabled={loading.paymentFund || loading.bankAccounts || (activeFundBankAccounts && activeFundBankAccounts.length === 0)}
                  className='btn btn-primary border-0 px-4 pb-2 d-flex align-items-center justify-content-center'
                  style={{
                    minHeight: "38px",
                    minWidth: "82px",
                    fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                  }}
                >
                  {loading.paymentFund ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm'
                        aria-hidden='true'
                        style={{
                          fontSize: "0.85rem",
                          height: screenSize >= 576 ? "15px" : "14px",
                          width: screenSize >= 576 ? "15px" : "14px",
                        }}
                      ></span>
                      {/* <span role="status">Loading...</span> */}
                    </>
                  ) : (
                    t("modals.common.next")
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* -- PAYMENT PROOF - MODAL -- */}
      <div className='modal fade' id='paymentProofModal' tabIndex='-1' aria-labelledby='paymentProofModal' aria-hidden='true' ref={paymentProofModal}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "640px",
            margin: screenSize >= 655 ? "auto" : ".5rem",
          }}
        >
          <form
            className='modal-content'
            onSubmit={handleFundPurchaseFinal}
            style={{
              backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(50, 50, 50)",
              border: "none",
            }}
          >
            <div
              className='modal-header align-items-center justify-content-between position-relative border-0'
              style={{
                backgroundColor: theme === "light" ? "rgb(101, 138, 181)" : "rgb(49, 101, 162)",
                minHeight: "67px",
                padding: screenSize >= 576 ? "1rem .8rem" : ".75rem .65rem",
              }}
            >
              <div
                className='ps-2'
                style={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: screenSize >= 576 ? "1rem" : ".82rem",
                }}
              >
                {t("modals.common.step")} 3<span style={{ margin: "0 .125rem" }}>/</span>3
              </div>

              <h1
                className='modal-title position-absolute start-50 translate-middle-x text-center text-nowrap'
                id='goalModal'
                style={{ color: "rgb(224, 232, 240)", fontSize: screenSize >= 576 ? "1.45rem" : "1.25rem", lineHeight: screenSize >= 576 ? "" : "1.1" }}
              >
                {t("modals.purchase.proof.title")}
              </h1>
              <button
                type='button'
                className='btn-close p-0 d-flex align-items-center justify-content-center'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  color: "rgb(255,255,255)",
                  background: "none",
                  height: screenSize >= 576 ? "32px" : "28px",
                  width: screenSize >= 576 ? "32px" : "28px",
                  ["--bs-btn-close-opacity"]: "0.65",
                  ["--bs-btn-close-hover-opacity"]: "0.85",
                }}
                onClick={togglePaymentProofModal}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height={screenSize >= 576 ? 18 : 16}
                  width={screenSize >= 576 ? 18 : 16}
                  stroke='currentColor'
                  fill='currentColor'
                  viewBox='0 0 16 16'
                >
                  <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
                </svg>
              </button>
            </div>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div className='row w-100 mx-auto gap-3 p-3 px-4 pb-2'>
                <div
                  className=''
                  style={{
                    fontSize: ".96rem",
                    color: "rgb(150,150,150)",
                  }}
                >
                  {/* {`Please ${allowedActions.includes(activeRequestData?.request_status) ? "update" : "upload"} the proof of the payment for this request.` + " "} */}
                  {t("app.messages.funds.proof")}
                  <span className='ms-2' style={{ color: "var(--theme-light-secondary)" }}>
                    {"(JPG, PNG, JPEG, WEBP)"}
                  </span>
                </div>
                <div className='col-12 p-0 py-3 pe-sm-1 py-sm-1'>
                  <ImageUpload allowedFileTypes={["image/jpeg", "image/png", "image/jpg", "image/webp"]} onFileChange={handlePaymentProofUpload} ref={imageUploadRef} />
                  {/* <div className='row align-items-center justify-content-center flex-sm-nowrap gap-2'>
                    <div className='col-12 col-sm-auto d-flex flex-column align-items-center justify-content-center flex-nowrap' style={{}}>
                      <input type='file' id='payment_proof_file' className='custom-file-input' onChange={handlePaymentProofUpload} />
                      <label
                        htmlFor='payment_proof_file'
                        className='custom-file-label form-control text-truncate pe-3'
                        style={{
                          ...themeBasedSignupFormControl(theme),
                          fontSize: "1rem",
                          width: screenSize >= 576 ? "110px" : "100%",
                          maxWidth: screenSize >= 576 ? "100%" : "120px",
                        }}
                      >
                        {fundPaymentData.payment_proof_file ? fundPaymentData.payment_proof_file.name : t("modals.purchase.proof.button")}
                      </label>
                    </div>
                    <div
                      className='col-12 col-sm-auto p-0 mt-1 mt-sm-0'
                      style={{
                        maxWidth: screenSize >= 655 ? "calc(100% - 490px)" : "112px",
                        fontSize: ".8rem",
                        lineHeight: "1.2",
                        color: theme === "light" ? "" : "rgba(255,255,255,.5)",
                      }}
                    >
                      {t("modals.purchase.proof.caption")}
                    </div>
                  </div> */}
                </div>

                <div className='col-12 p-0'>
                  <hr className='my-2 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                </div>
              </div>
            </div>
            <div className='modal-footer border-0 flex-nowrap justify-content-between'>
              <button
                type='button'
                className='btn border-0 px-3 px-sm-4 pb-2'
                onClick={handleFundPaylater}
                style={{
                  ...customModalCancelButton,
                  fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                  minHeight: screenSize >= 576 ? "38px" : "35px",
                  minWidth: screenSize >= 576 ? "80px" : "90px",
                }}
              >
                {t("modals.purchase.proof.later")}
              </button>
              <button
                type='submit'
                disabled={loading.finalPaymentFund}
                className='btn btn-primary border-0 px-3 px-sm-4 pb-2 d-flex align-items-center justify-content-center'
                style={{
                  fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                  minHeight: screenSize >= 576 ? "38px" : "35px",
                  minWidth: screenSize >= 576 ? "80px" : "90px",
                }}
              >
                {loading.finalPaymentFund ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: screenSize >= 576 ? "15px" : "14px",
                        width: screenSize >= 576 ? "15px" : "14px",
                      }}
                    ></span>
                    {/* <span role="status">Loading...</span> */}
                  </>
                ) : (
                  t("modals.common.submit")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* -- PAYMENT CHECK - MODAL -- */}
      <div className='modal fade' id='paymentCheckModal' tabIndex='-1' aria-labelledby='paymentCheckModal' aria-hidden='true' ref={paymentCheckModal}>
        <div
          className='modal-dialog modal-dialog-centered'
          style={{
            ["--bs-modal-width"]: "500px",
            margin: screenSize >= 576 ? "auto" : ".55rem",
          }}
        >
          <form
            className='modal-content'
            onSubmit={() => {}}
            style={{
              backgroundColor: theme === "light" ? "rgb(243, 243, 243)" : "rgb(62, 62, 62)",
              boxShadow: `0 0 0px 2px ${theme === "light" ? "rgb(112, 112, 112)" : "rgb(78, 78, 78)"}`,
              border: "none",
            }}
          >
            <div
              className='modal-header align-items-center justify-content-end position-relative border-0'
              style={{
                backgroundColor: theme === "light" ? "rgb(101, 138, 181)" : "rgb(129, 105, 33)",
                minHeight: "67px",
                padding: screenSize >= 576 ? "1rem .8rem" : ".75rem .65rem",
              }}
            >
              {/* <div className="ps-2" style={{
								color: 'rgba(255,255,255,.7)',
							}}>
								Step 3<span style={{ margin: '0 .125rem' }}>/</span>3
							</div> */}

              <h1
                className='modal-title position-absolute start-50 translate-middle-x text-center text-nowrap'
                id='goalModal'
                style={{ color: "rgb(224, 232, 240)", fontSize: screenSize >= 576 ? "1.45rem" : "1.25rem", lineHeight: screenSize >= 576 ? "" : "1.1" }}
              >
                {t("modals.purchase.confirm.title")}
              </h1>
              <button
                type='button'
                className='btn-close p-0 d-flex align-items-center justify-content-center'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  color: "rgb(255,255,255)",
                  background: "none",
                  height: screenSize >= 576 ? "32px" : "28px",
                  width: screenSize >= 576 ? "32px" : "28px",
                  ["--bs-btn-close-opacity"]: "0.65",
                  ["--bs-btn-close-hover-opacity"]: "0.85",
                }}
                onClick={() => {
                  if (paymentCheckModal.current) {
                    paymentCheckModalInstance.current.hide();
                  }
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height={screenSize >= 576 ? 18 : 16}
                  width={screenSize >= 576 ? 18 : 16}
                  stroke='currentColor'
                  fill='currentColor'
                  viewBox='0 0 16 16'
                >
                  <path d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414'></path>
                </svg>
              </button>
            </div>

            <hr className='m-0' />

            <div className='modal-body p-0'>
              <div
                className='d-flex align-items-center justify-content-center px-2 pt-2'
                style={{
                  minHeight: "130px",
                  color: theme === "dark" ? "rgba(255,255,255,.9)" : "",
                  fontSize: screenSize >= 576 ? "1.1rem" : "1rem",
                }}
              >
                {t("modals.purchase.confirm.message")}
              </div>
            </div>
            <div className='modal-footer border-0 flex-nowrap justify-content-between'>
              <button
                type='button'
                className='btn border-0 px-3 px-sm-4 pb-2'
                onClick={handleFundPaymentDecline}
                style={{
                  ...customModalCancelButton,
                  fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                  minHeight: screenSize >= 576 ? "38px" : "35px",
                  minWidth: screenSize >= 576 ? "80px" : "90px",
                }}
              >
                {t("modals.purchase.confirm.decline")}
              </button>
              <button
                type='button'
                onClick={handleFundPaymentAccept}
                className='btn border-0 px-3 px-sm-4 pb-2'
                style={{
                  ...customAcceptButton,
                  fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                  minHeight: screenSize >= 576 ? "38px" : "35px",
                  minWidth: screenSize >= 576 ? "80px" : "90px",
                }}
              >
                {t("modals.purchase.confirm.accept")}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* -- CALCULATOR - MODAL -- */}
      <div className='modal fade' id='helpModal' tabIndex='-1' aria-labelledby='helpModal' aria-hidden='true' ref={helpModal}>
        <div
          className='modal-dialog modal-dialog-centered m-2 mx-md-auto'
          style={{
            ["--bs-modal-width"]: "700px",
            marginInline: screenSize >= 655 ? "auto" : ".5rem",
          }}
        >
          <div
            className='modal-content position-relative'
            style={{
              backgroundColor: theme === "light" ? "rgb(230 230 230)" : "rgb(50 ,50 ,50)",
              color: theme === "light" ? "rgb(0,0,0)" : "rgb(232, 232, 232)",
            }}
          >
            <div
              className='position-absolute top-0 end-0'
              style={{
                zIndex: "100",
              }}
            >
              <button
                type='button'
                className='btn p-1'
                aria-label='Close'
                style={{
                  marginLeft: "0",
                  marginRight: ".15rem",
                  border: "none",
                  boxShadow: "none",
                }}
                onClick={() => {
                  if (helpModal.current) {
                    helpModalInstance.current.toggle();
                  }
                }}
              >
                <CrossIcon width={32} height={32} color={theme === "dark" ? "rgba(225,225,225,.7)" : "rgba(34, 34, 34, 0.8)"} />
              </button>
            </div>

            <div className='modal-body p-2 p-sm-3'>
              <div
                className='row w-100 mx-auto text-center flex-column align-items-center justify-content-center flex-nowrap row-gap-3 pt-2 pb-2 pb-sm-3 px-2 px-sm-3'
                style={{
                  minHeight: "160px",
                }}
              >
                <div
                  className='col-auto p-0'
                  style={{
                    color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                    fontSize: "1.6rem",
                    fontWeight: "500",
                    lineHeight: "1.15",
                    maxWidth: "400px",
                  }}
                >
                  {t("invest.tooltip.title")}
                </div>

                <div
                  className='text-start col-12 p-0'
                  style={{
                    color: theme === "dark" ? "rgba(255,255,255,.75)" : "rgba(0,0,0,.75)",
                  }}
                >
                  <span style={{ marginBottom: "12px", marginInlineEnd: "6px" }}>{t("invest.tooltip.A")}</span>
                  <span className={`collapse collapse-inline ${!collapsedText ? "show" : ""}`} aria-hidden={collapsedText} style={{ marginLeft: 6 }}>
                    {/* <div className={`collapse ${!collapsedText ? "show" : ""}`}> */}
                    <ul
                      style={{
                        listStyleType: "disc",
                        padding: 0,
                        paddingLeft: "18px",
                        margin: isRTL ? "24px 0" : "12px 0",
                      }}
                    >
                      <li style={{ marginBottom: "4px" }}>{t("invest.tooltip.B")}</li>
                      <li>{t("invest.tooltip.C")}</li>
                    </ul>
                    {t("invest.tooltip.D")}
                    {/* </div> */}
                  </span>
                  {/* <div className="mt-2 text-end"> */}
                  <button
                    type='button'
                    className='btn btn-link p-0 inline-toggle align-baseline'
                    aria-expanded={!collapsedText}
                    aria-controls='moreText'
                    onClick={() => setCollapsedText((s) => !s)}
                    style={{
                      ["--bs-link-color"]: theme === "dark" ? "var(--theme-light-secondary)" : "",
                      ["--bs-link-hover-color"]: theme === "dark" ? "rgba(255,255,255,.7)" : "",
                      ["--bs-btn-active-color"]: theme === "dark" ? "var(--theme-light-secondary)" : "",
                    }}
                  >
                    {!collapsedText ? t("modals.calculator.less") : t("modals.calculator.more")}
                  </button>
                  {/* </div> */}
                </div>

                <div className='col-12 p-0'>
                  <hr className='mt-2 mb-0 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                </div>

                <div className='col-12 p-0'>
                  <div
                    className='text-start mb-2'
                    style={{
                      fontSize: "18px",
                      fontWeight: isRTL ? "500" : "300",
                      letterSpacing: ".03em",
                      color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                    }}
                  >
                    {t("modals.calculator.select")}
                  </div>
                  <div
                    className=''
                    style={{
                      padding: "12px",
                      border: "1px solid",
                      backgroundColor: theme === "dark" ? "rgba(0,0,0,.15)" : "rgba(212, 218, 234, 0.88)",
                      borderColor: theme === "dark" ? "rgba(173, 158, 174, 0.4)" : "rgba(155, 160, 173, 0.88)",
                      borderRadius: "20px",
                    }}
                  >
                    <div className='d-flex w-100 align-items-center flex-nowrap gap-1 gap-sm-2'>
                      <div
                        className='flex-grow-1 position-relative'
                        style={{
                          // maxWidth: 'calc(100% - 40px)',
                          overflow: "hidden",
                          minWidth: "0",
                        }}
                      >
                        {categoryScrollLeft ? (
                          <button
                            className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                            onClick={() => {
                              calculatorCategoryRef.current.scrollLeft -= 60;
                            }}
                            style={{
                              top: screenSize >= 576 ? "-3px" : "0px",
                              left: screenSize >= 576 ? "3px" : "0px",
                              color: "var(--theme-light-secondary)",
                              padding: "2px",
                              background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(214, 219, 234)",
                              boxShadow:
                                theme === "dark"
                                  ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) -13px 0px 4px 8px"
                                  : "rgb(214, 219, 234) 0px 0px 12px 14px, rgb(214, 219, 234) -12px 0px 0px 6px",
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={screenSize >= 576 ? 30 : 24}
                              height={screenSize >= 576 ? 30 : 24}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M15 6l-6 6l6 6' />
                            </svg>
                          </button>
                        ) : (
                          ""
                        )}

                        {categoryScrollRight ? (
                          <button
                            className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                            onClick={() => {
                              calculatorCategoryRef.current.scrollLeft += 60;
                            }}
                            style={{
                              top: screenSize >= 576 ? "-3px" : "0px",
                              right: screenSize >= 576 ? "3px" : "0px",
                              color: "var(--theme-light-secondary)",
                              padding: "2px",
                              background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(214, 219, 234)",
                              boxShadow:
                                theme === "dark"
                                  ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) 13px 0px 4px 8px"
                                  : "rgb(214, 219, 234) 0px 0px 12px 14px, rgb(214, 219, 234) 13px 0px 4px 8px",
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={screenSize >= 576 ? 30 : 24}
                              height={screenSize >= 576 ? 30 : 24}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M9 6l6 6l-6 6' />
                            </svg>
                          </button>
                        ) : (
                          ""
                        )}

                        <div
                          className='d-flex w-100 align-items-center flex-nowrap gap-2'
                          ref={calculatorCategoryRef}
                          style={{
                            overflowX: "auto",
                            scrollbarWidth: "none",
                          }}
                        >
                          <button
                            className={`btn${selectFund.category?.name === "All" ? " active" : ""}`}
                            onClick={() => setSelectFund((prev) => ({ ...prev, category: { name: "All" }, search: "" }))}
                            style={{
                              paddingInline: "13px",
                              paddingBlock: "4.5px 4px",
                              borderRadius: "999px",
                              textWrap: "nowrap",
                              fontSize: "13px",
                              letterSpacing: ".04em",
                              // backgroundColor: selectFund.category?.name === "All" ? (theme === 'dark' ? "rgba(203, 110, 209, .6)" : "rgb(55, 131, 223)") : "",
                              ...tabButton(theme),
                              // minWidth: '120px',
                              // maxWidth: '200px'
                            }}
                          >
                            {t("requests.tabs.all")}
                          </button>
                          {fundCategories?.map((category, idx) => (
                            <button
                              className={`btn${selectFund.category?.name === category?.name ? " active" : ""}`}
                              key={idx}
                              onClick={() => {
                                setSelectFund((prev) => ({ ...prev, category, search: "" }));

                                if (calculatorFundRef.current) {
                                  calculatorFundRef.current.scrollLeft = 0;
                                }
                              }}
                              style={{
                                paddingInline: "13px",
                                paddingBlock: "4.5px 4px",
                                borderRadius: "999px",
                                textWrap: "nowrap",
                                fontSize: "13px",
                                letterSpacing: ".04em",
                                // backgroundColor: selectFund.category?.name === category?.name ? (theme === 'dark' ? "rgba(203, 110, 209, .6)" : "rgb(55, 131, 223)") : "",
                                ...tabButton(theme),
                                // minWidth: '120px',
                                // maxWidth: '200px'
                              }}
                            >
                              {category.name}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className='flex-shrink-0 flex-grow-0'>
                        {showSearchSelectFund ? (
                          <div
                            className='position-relative'
                            style={{
                              "--bs-border-color": "rgba(255,255,255,.4)",
                              "--bs-body-color": "rgba(255,255,255,.7)",
                              "--bs-body-bg": "rgba(255, 255, 255, 0)",
                            }}
                          >
                            <input
                              type='text'
                              className='form-control investment-search'
                              placeholder={t("app.messages.search.glossary")}
                              value={selectFund.search}
                              onChange={(e) => {
                                setSelectFund((prev) => ({ ...prev, search: e.target.value }));
                              }}
                              onBlur={() => setShowSearchSelectFund(false)}
                              aria-label='Search Term ...'
                              aria-describedby='button-addon2'
                              ref={selectSearchFundRef}
                              style={{
                                minWidth: "260px",
                                color: theme === 'dark' ? "rgba(255,255,255,.7)" : "rgba(36, 36, 36, 0.7)",
                                borderRadius: "999px",
                                paddingInline: isRTL ? "2.3rem .75rem" : ".75rem 2.3rem",
                              }}
                            />
                            <button
                              className='btn h-100 border-0 position-absolute'
                              type='button'
                              id='button-addon2'
                              style={{
                                top: "0",
                                right: "0",
                                padding: ".25rem .5rem .45rem",
                              }}
                            >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width={22}
                                height={22}
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='var(--theme-light-secondary)'
                                strokeWidth={1.5}
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-search'
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
                                <path d='M21 21l-6 -6' />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <button
                            className='btn h-100 border-0'
                            type='button'
                            id='button-addon2'
                            onClick={() => {
                              setShowSearchSelectFund(true);
                            }}
                            style={{
                              padding: ".25rem .5rem .45rem",
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={22}
                              height={22}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='var(--theme-light-secondary)'
                              strokeWidth={1.5}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-search'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
                              <path d='M21 21l-6 -6' />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className=''>
                      <hr className='mx-1' style={{ marginBlock: "12px", color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                    </div>
                    <div className='position-relative overflow-hidden'>
                      {fundScrollLeft ? (
                        <button
                          className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                          onClick={() => {
                            calculatorFundRef.current.scrollLeft -= 60;
                          }}
                          style={{
                            top: "-2px",
                            bottom: "0px",
                            left: "-8px",
                            color: "var(--theme-light-secondary)",
                            zIndex: "100",
                            padding: "2px",
                            background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(214, 219, 234)",
                            boxShadow:
                              theme === "dark"
                                ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) -13px 0px 4px 8px"
                                : "rgb(214, 219, 234) 0px 0px 12px 14px, rgb(214, 219, 234) -12px 0px 0px 6px",
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={30}
                            height={30}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M15 6l-6 6l6 6' />
                          </svg>
                        </button>
                      ) : (
                        ""
                      )}

                      {fundScrollRight ? (
                        <button
                          className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                          onClick={() => {
                            calculatorFundRef.current.scrollLeft += 60;
                          }}
                          style={{
                            top: "-2px",
                            bottom: "0px",
                            right: "-8px",
                            color: "var(--theme-light-secondary)",
                            padding: "2px",
                            zIndex: "100",
                            background: theme === "dark" ? "rgb(43, 43, 43)" : "rgb(214, 219, 234)",
                            boxShadow:
                              theme === "dark"
                                ? "rgb(43, 43, 43) 0px 0px 12px 14px, rgb(43, 43, 43) 13px 0px 4px 8px"
                                : "rgb(214, 219, 234) 0px 0px 12px 14px, rgb(214, 219, 234) 13px 0px 4px 8px",
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={30}
                            height={30}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M9 6l6 6l-6 6' />
                          </svg>
                        </button>
                      ) : (
                        ""
                      )}
                      <div
                        className='d-flex align-items-center flex-nowrap'
                        ref={calculatorFundRef}
                        style={{
                          gap: "13px",
                          overflowX: "auto",
                          scrollbarWidth: "none",
                        }}
                      >
                        {searchSelectFunds?.map((item, idx) => (
                          <>
                            <button
                              className={`btn${selectFund.fund?.symbol_code === item?.symbol_code ? " active" : ""}`}
                              type='button'
                              key={idx}
                              onClick={() => {
                                setSelectFund((prev) => {
                                  if (prev.total_amount !== "") {
                                    const units = format4(parseFloat(prev.total_amount) / parseFloat(item?.navData?.offer_price));

                                    return { ...prev, fund: item, fund_units: units };
                                  }
                                  return { ...prev, fund: item };
                                });
                                // setActivePurchaseFund(item);
                              }}
                              style={{
                                padding: "10px 14px",
                                borderRadius: "12px",
                                width: "200px",
                                minWidth: "200px",
                                maxWidth: "200px",
                                // backgroundColor: selectFund.fund?.symbol_code === item?.symbol_code ? (theme === 'dark' ? "rgba(203, 110, 209, .075)" : 'var(--theme-light-primary)') : "",
                                // borderColor: selectFund.fund?.symbol_code === item?.symbol_code ? "rgba(203, 110, 209, .4)" : "",
                                ...fundButton(theme),
                              }}
                            >
                              <div className='text-start position-relative' style={{ lineHeight: "1.1" }}>
                                <div
                                  className=''
                                  style={{
                                    fontSize: "22px",
                                    color: theme === "dark" ? "var(--theme-light-secondary)" : "",
                                    marginBlockEnd: "2px",
                                  }}
                                >
                                  {item?.symbol_code}
                                </div>
                                <div
                                  className='text-truncate'
                                  data-tooltip-id={"tt-simple"}
                                  data-tooltip-content={item?.fund_name}
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "300",
                                    color: theme === "dark" ? "rgba(255,255,255,.6)" : "",
                                  }}
                                >
                                  {item?.fund_name}
                                </div>

                                <div className='position-absolute top-0 end-0'>
                                  <div
                                    className=''
                                    dir='ltr'
                                    style={{
                                      fontSize: "12px",
                                      color: theme === "dark" ? "rgba(255,255,255,.7)" : "",
                                    }}
                                  >
                                    <span>@</span>
                                    <span dir='ltr' style={{ marginInlineStart: "4px", color: theme === "dark" ? "" : "" }}>
                                      {t("app.fund.rupeeAlt")}
                                    </span>
                                    <span dir='ltr' style={{ marginInlineStart: "4px", fontSize: "15px", color: theme === "dark" ? "var(--theme-light-secondary)" : "" }}>
                                      {item?.navData?.nav || "0"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {/* <div className=''>
                                    <hr className='my-2 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                                  </div>
                                  <div className="d-flex align-items-center justify-content-between flex-nowrap">
                                    <div className="" style={{
                                      fontSize: '14px',
                                      color: theme === 'dark' ? 'rgba(255,255,255,.6)' : ''
                                    }}>
                                      Price:
                                    </div>
                                    <div className="" style={{
                                      fontSize: '14px',
                                      color: theme === 'dark' ? 'var(--theme-light-secondary)' : ''
                                    }}>
                                      {item?.navData?.nav || '0'}
                                    </div>
                                  </div> */}
                            </button>
                          </>
                        ))}
                        <Tooltip
                          // anchorSelect={"asd"}
                          id={"tt-simple"}
                          place='bottom'
                          // offset={12}
                          // delayShow={120}
                          className='my-rt'
                          classNameArrow='my-rt-arrow'
                          border='1px solid rgba(255,255,255,0.12)'
                          arrowSize={8}
                          opacity={0.95}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-12 p-0'>
                  <hr className='my-0 mx-1' style={{ color: theme === "light" ? "rgb(33, 37, 41)" : "rgb(134, 151, 168)" }} />
                </div>

                <div className='col-12 p-0'>
                  <div
                    className='d-flex flex-column align-items-center justify-content-center flex-nowrap mx-auto'
                    style={{
                      minHeight: "180px",
                      maxWidth: "480px",
                    }}
                  >
                    <div className='w-100 mx-auto mt-3'>
                      <div
                        className='text-center mt-0'
                        style={{
                          fontSize: screenSize >= 576 ? "16px" : ".78rem",
                          color: theme === "light" ? "rgb(143, 144, 145)" : "rgba(186, 187, 189, 1)",
                          fontWeight: "400",
                          marginBlockEnd: "14px",
                          letterSpacing: ".03em",
                          lineHeight: "1",
                        }}
                      >
                        {t("modals.purchase.info.amount")}
                      </div>
                      <div className='position-relative'>
                        {amountScrollLeft ? (
                          <button
                            className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                            onClick={() => {
                              calculatorAmountRef.current.scrollLeft -= 60;
                            }}
                            style={{
                              top: "-3px",
                              left: "3px",
                              color: "var(--theme-light-secondary)",
                              padding: "2px",
                              background: theme === "dark" ? "rgb(50, 50, 50)" : "rgb(214, 219, 234)",
                              boxShadow:
                                theme === "dark"
                                  ? "rgb(50, 50, 50) 0px 0px 6px 4px, rgb(50, 50, 50) -2px 0px 2px 4px"
                                  : "rgb(214, 219, 234) 0px 0px 6px 4px, rgb(214, 219, 234) -2px 0px 2px 4px",
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={24}
                              height={24}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M15 6l-6 6l6 6' />
                            </svg>
                          </button>
                        ) : (
                          ""
                        )}

                        {amountScrollRight ? (
                          <button
                            className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                            onClick={() => {
                              calculatorAmountRef.current.scrollLeft += 60;
                            }}
                            style={{
                              top: "-3px",
                              right: "3px",
                              color: "var(--theme-light-secondary)",
                              padding: "2px",
                              background: theme === "dark" ? "rgb(50, 50, 50)" : "rgb(214, 219, 234)",
                              boxShadow:
                                theme === "dark"
                                  ? "rgb(50, 50, 50) 0px 0px 6px 4px, rgb(50, 50, 50) 6px 0px 2px 4px"
                                  : "rgb(214, 219, 234) 0px 0px 6px 4px, rgb(214, 219, 234) 6px 0px 2px 4px",
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={24}
                              height={24}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M9 6l6 6l-6 6' />
                            </svg>
                          </button>
                        ) : (
                          ""
                        )}
                        <div
                          className='w-100 d-flex align-items-center flex-nowrap overflow-auto gap-2 px-3 mb-2'
                          ref={calculatorAmountRef}
                          style={{
                            scrollbarWidth: "none",
                          }}
                        >
                          {["1000", "10000", "50000", "100000", "1000000"].map((num, idx) => {
                            return (
                              <button
                                className={`btn${parseFloat(selectFund.total_amount) === parseFloat(num) ? " active" : ""}`}
                                value={num}
                                key={idx}
                                name='total_amount'
                                onClick={(e) => handleCalculatorChange(e)}
                                type='button'
                                style={{
                                  fontSize: "13px",
                                  paddingBlock: "3px",
                                  fontWeight: "300",
                                  lineHeight: "1.25",
                                  flex: "1",
                                  borderRadius: "999px",
                                  ...tabButton(theme),
                                }}
                              >
                                {numberWithCommas(num)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <input
                        type='text'
                        className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                        name='total_amount'
                        value={selectFund.total_amount && numberWithCommas(selectFund.total_amount)}
                        style={{
                          ...themeBasedPurchaseFormControl(theme),
                          marginTop: ".4rem",
                          fontSize: screenSize >= 576 ? "22px" : "1rem",
                        }}
                        onChange={handleCalculatorChange}
                      />
                    </div>
                    <div
                      className='text-center mt-2'
                      style={{
                        fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                        minHeight: "14px",
                        color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                        fontWeight: "400",
                        lineHeight: "1",
                      }}
                    >
                      {selectFund.total_amount ? amountToWords(selectFund.total_amount) : ""}
                    </div>
                    <div className='row w-100 mx-auto mt-4'>
                      <div className='col p-0 pe-1'>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("app.fund.price")}
                        </div>
                        <input
                          type='text'
                          disabled
                          className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                          value={format4(selectFund.fund?.navData?.offer_price || 0)}
                          style={{
                            ...themeBasedPurchaseFormControl(theme),
                            marginTop: ".4rem",
                            fontSize: screenSize >= 576 ? "18px" : "1rem",
                          }}
                        />
                      </div>
                      <div className='col-auto p-0'>
                        <div className='d-flex flex-column justify-content-end align-items-center h-100 pb-2'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={26}
                            height={26}
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='var(--theme-light-primary)'
                            strokeWidth={1.5}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-x'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M18 6l-12 12' />
                            <path d='M6 6l12 12' />
                          </svg>
                        </div>
                      </div>
                      <div className='col p-0 ps-1'>
                        <div
                          className='text-center'
                          style={{
                            fontSize: screenSize >= 576 ? ".82rem" : ".78rem",
                            color: theme === "light" ? "rgb(143, 144, 145)" : "rgb(166, 167, 169)",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {t("modals.purchase.info.units")}
                        </div>
                        <input
                          type='text'
                          name='fund_units'
                          value={selectFund.fund_units && numberWithCommas(selectFund.fund_units)}
                          className={`form-control goal-form-control${theme === "dark" ? "-dark" : ""} rounded-pill text-center`}
                          style={{
                            ...themeBasedPurchaseFormControl(theme),
                            marginTop: ".4rem",
                            fontSize: screenSize >= 576 ? "18px" : "1rem",
                          }}
                          onChange={handleCalculatorChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-footer border-0 flex-nowrap justify-content-between'>
              <button
                type='button'
                disabled={selectFund.fund === null || selectFund.total_amount === ""}
                className='btn border-0 px-3 py-0 d-flex align-items-center justify-content-center'
                onClick={() => {
                  if (!user) {
                    if (helpModal.current) {
                      helpModalInstance.current.toggle();
                    }

                    navigate("/unlock");
                    return;
                  } else if (user?.requiresDocumentVerification) {
                    if (helpModal.current) {
                      helpModalInstance.current.toggle();
                    }

                    navigate("/verify");
                    return;
                  } else {
                    let checkUnitsZero = false;
                    let checkAmountZero = false;

                    if (selectFund.fund_units && selectFund.fund_units !== "" && selectFund.fund_units?.length !== 0) {
                      if (parseFloat(selectFund.fund_units) === 0 || parseFloat(selectFund.fund_units) < 1) {
                        checkUnitsZero = true;
                      }
                    } else {
                      checkUnitsZero = true;
                    }

                    if (selectFund.total_amount && selectFund.total_amount !== "" && selectFund.total_amount?.length !== 0) {
                      if (parseFloat(selectFund.total_amount) === 0 || parseFloat(selectFund.total_amount) < 1) {
                        checkAmountZero = true;
                      }
                    } else {
                      checkAmountZero = true;
                    }

                    if (checkUnitsZero || checkAmountZero) {
                      toast((ts) => customToast("Please enter a valid value greater than zero to proceed with the transaction.", "error", ts), {
                        duration: 4000,
                        removeDelay: 1000,
                        style: {
                          padding: "0.75rem",
                          margin: "0",
                          width: "400px",
                          maxWidth: "100%",
                          // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
                          // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
                          color: "rgb(33,37,41)",
                          backgroundColor: "rgb(221, 204, 225)",
                        },
                      });

                      toast((ts) => customToast("Amount and units cannot be zero.", "error", ts), {
                        duration: 4000,
                        removeDelay: 1000,
                        style: {
                          padding: "0.75rem",
                          margin: "0",
                          width: "340px",
                          maxWidth: "100%",
                          // color: theme === 'light' ? 'rgb(33,37,41)' : 'rgb(230, 230, 230)',
                          // backgroundColor: theme === 'light' ? 'rgba(255,255,255,.9)' : 'rgb(51, 34, 61)'
                          color: "rgb(33,37,41)",
                          backgroundColor: "rgb(221, 204, 225)",
                        },
                      });

                      return;
                    }

                    if (parseFloat(selectFund.fund_units)) purchaseFromCalculator.current = true;
                    setActivePurchaseFund(selectFund.fund);
                    setPurchaseData(() => ({
                      fund_units: selectFund.fund_units,
                      total_amount: selectFund.total_amount,
                    }));

                    purchaseProcess.current = "payment";
                    // backButtonPress.current = false;

                    if (helpModal.current) {
                      helpModalInstance.current.toggle();
                    }

                    togglePaymentModal();
                  }
                }}
                style={{
                  ...customModalCancelButton,
                  minHeight: "38px",
                  minWidth: "80px",
                  fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                }}
              >
                {loading.purchaseFund ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: screenSize >= 576 ? "15px" : "14px",
                        width: screenSize >= 576 ? "15px" : "14px",
                      }}
                    ></span>
                    {/* <span role="status">Loading...</span> */}
                  </>
                ) : (
                  t("app.fund.purchase")
                )}
              </button>

              <button
                type='button'
                disabled={loading.purchaseFund}
                className='btn btn-primary border-0 p-0 px-3 d-flex align-items-center justify-content-center'
                onClick={() => {
                  if (helpModal.current) {
                    helpModalInstance.current.toggle();
                  }
                }}
                style={{
                  minHeight: "38px",
                  minWidth: "80px",
                  fontSize: screenSize >= 576 ? "1rem" : ".9rem",
                }}
              >
                {loading.purchaseFund ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      aria-hidden='true'
                      style={{
                        fontSize: "0.85rem",
                        height: screenSize >= 576 ? "15px" : "14px",
                        width: screenSize >= 576 ? "15px" : "14px",
                      }}
                    ></span>
                    {/* <span role="status">Loading...</span> */}
                  </>
                ) : (
                  t("modals.common.close")
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* <FundDetailsModal
        show={showDetails?.show}
        symbol={showDetails?.symbol}
        backdrop={true}
        keyboard={true}
        onClose={() => setShowDetails(() => ({ show: false, symbol: "" }))}
      /> */}

      {!funds.length ? (
        <div
          className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
          style={{
            color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
          }}
        >
          <div
            className=''
            role='status'
            style={{
              fontSize: "1.2rem",
            }}
          >
            {t("app.loading.funds")}
          </div>
          <div
            className='spinner-border'
            style={{
              fontSize: "0.8rem",
              height: "30px",
              width: "30px",
              color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
            }}
          >
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default Funds;
